/*
 * PF for PowerPoint — task pane bridge.
 *
 * Thin Office.js bridge between the LIVE PowerPoint presentation and the Wippy
 * chat that runs in the embedded iframe. Forked from the Excel add-in shell;
 * unlike Excel this is a HYBRID bridge: targeted reads/edits run live via
 * `PowerPoint.run`, while whole designed slides arrive as a pptx DSL that we
 * render with PptxGenJS and inject via `insertSlidesFromBase64` (the only
 * route to native editable charts), and composite graphics (org charts,
 * timelines, process flows) are expanded by pure layout engines into grouped
 * native shapes.
 *
 * Protocol mirrors the Word/Excel/Outlook add-ins: a single
 * `{ type: "@office-plugin", action, ... }` envelope, both directions, over
 * postMessage against CHAT_ORIGIN.
 */

/// <reference types="office-js" />

/* global document, Office, PowerPoint, window, console, fetch */

import { normalizeOps } from "./ops";
import { canHoldText, isGroupType, isPlaceholderType, isTableType } from "./shapeTypes";
import { renderSlidesToBase64, DslSlide } from "./dslRenderer";
import { orgChartLayout } from "./layout/orgChart";
import { timelineLayout } from "./layout/timeline";
import { processFlowLayout } from "./layout/processFlow";
import { structureChartLayout } from "./layout/structureChart";
import { Primitive, Region, DEFAULT_PALETTE, DEFAULT_REGION } from "./layout/primitives";
import { estimateTextFit, overflowWarning } from "./textFit";
import { isShrinkToFit } from "./autofit";
import { parseTableValues, CellStyle } from "./tableValues";
import { computeImageFit, FitMode } from "./imageFit";
import { planSegments, TextSegment } from "./textSegments";
import { planFontFloor, SizeRead } from "./fontFloor";
import { hasStrayDoublePunct } from "./textHygiene";

declare const WIPPY_IFRAME_URL: string;
declare const WIPPY_AGENT_ID: string;
/** Git sha of this bundle, injected at build time. Echoed on inspect_deck so a
 * stale cached pane is identifiable from the agent transcript alone. */
declare const PANE_BUILD: string;

/** Allowed origin for the embedded chat (must match iframe src). */
const CHAT_ORIGIN = new URL(WIPPY_IFRAME_URL).origin;

/**
 * The iframe src. We carry the target agent as an `agent` query param so that
 * whichever surface the iframe points at can honor it:
 *   - the launcher view (ppt_editor) hardcodes the same agent and ignores it;
 *   - a generic popup shell can read ?agent=<id> to open that agent directly.
 */
function buildIframeSrc(): string {
  try {
    const u = new URL(WIPPY_IFRAME_URL);
    if (WIPPY_AGENT_ID) u.searchParams.set("agent", WIPPY_AGENT_ID);
    return u.toString();
  } catch {
    return WIPPY_IFRAME_URL;
  }
}

/** Salesforce OAuth popup config — carried over from the Word add-in shell. */
const DEFAULT_REDIRECT_URI = "https://salesforce-office-poc.vercel.app/callback.html";
const DEFAULT_LOGIN_URL = "https://login.salesforce.com";
const SANDBOX_LOGIN_URL = "https://test.salesforce.com";
const LOGIN_PATH = "/services/oauth2/authorize";

let wsReady = false;

/**
 * Slide ids present when the pane first bound the deck. Deleting any of these
 * (the user's/firm's pre-existing content — possibly a branded template)
 * requires an explicit confirm:true on the delete_slide op, so an agent must
 * ask the user before destroying template slides. Agent-created slides
 * (ids not in this set) delete freely.
 */
let originalSlideIds: Set<string> | null = null;

/** Requirement-set gates, resolved once at startup. Floor for rich ops is 1.8. */
const api = {
  v15: false, // selection
  v18: false, // tables, groups, z-order, moveTo, export/getImageAsBase64, picture fill
  v19: false, // table formatting / row-col mutation
  v110: false, // backgrounds, theme colors, hyperlink write, rotation
};

Office.onReady((info) => {
  if (info.host !== Office.HostType.PowerPoint) return;

  api.v15 = Office.context.requirements.isSetSupported("PowerPointApi", "1.5");
  api.v18 = Office.context.requirements.isSetSupported("PowerPointApi", "1.8");
  api.v19 = Office.context.requirements.isSetSupported("PowerPointApi", "1.9");
  api.v110 = Office.context.requirements.isSetSupported("PowerPointApi", "1.10");
  if (!api.v18) {
    console.warn("PowerPointApi 1.8 not supported on this build — rich editing ops will be limited.");
  }

  (document.getElementById("chat-iframe") as HTMLIFrameElement).src = buildIframeSrc();
  document.getElementById("sideload-msg").style.display = "none";
  document.getElementById("app-body").style.display = "flex";

  initPptBridge();

  // Poll the iframe until its WebSocket to Wippy is ready (== OAuth complete),
  // exactly like the Word/Excel shells. Once ready we push the deck context.
  const poll = setInterval(() => {
    if (wsReady) {
      clearInterval(poll);
      return;
    }
    notifyIframe({ type: "@office-plugin", action: "check_ws_status" });
  }, 500);
});

/**
 * PowerPoint Bridge — two-way communication between the deck and Wippy.
 *
 * PowerPoint → Wippy: on load / selection change we send the current deck
 *   context as `ppt_context` so the agent session can anchor on it.
 * Wippy → PowerPoint: the agent issues read queries (`inspect_deck`,
 *   `read_slide`, `get_selection`, `find_in_deck`, `get_slide_image`) and
 *   writes (`apply_ops`, `insert_slides`) that we execute against the deck.
 */
function initPptBridge(): void {
  try {
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () =>
      sendPresentationContext()
    );
  } catch {
    /* selection events are best-effort */
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (event.origin !== CHAT_ORIGIN) return;

    const data = typeof event.data === "string" ? safeParse(event.data) : event.data;
    if (!data || data.type !== "@office-plugin") return;

    switch (data.action) {
      case "ws_connected":
        // WebSocket connected in the iframe = OAuth complete.
        wsReady = true;
        sendPresentationContext();
        break;
      case "request_presentation":
        sendPresentationContext();
        break;
      case "inspect_deck":
        handleInspectDeck(data);
        break;
      case "read_slide":
        handleReadSlide(data);
        break;
      case "get_selection":
        handleGetSelection(data);
        break;
      case "find_in_deck":
        handleFindInDeck(data);
        break;
      case "get_slide_image":
        handleGetSlideImage(data);
        break;
      case "apply_ops":
        handleApplyOps(data);
        break;
      case "insert_slides":
        handleInsertSlides(data);
        break;
      case "insert_from_base64":
        handleInsertFromBase64(data);
        break;
      case "export_deck":
        handleExportDeck(data);
        break;
      case "get_theme":
        handleGetTheme(data);
        break;
      case "open_auth":
        // Optional: let the iframe trigger the Salesforce OAuth popup.
        openAuthDialog();
        break;
    }
  });
}

/* ── Deck context anchor ──────────────────────────────────────────────────── */

/**
 * Read a shape's text without throwing on text-less shape types.
 *
 * NOTE: the host sends the PascalCase enum VALUE ("GeometricShape"), not the
 * camelCase key. All type tests go through `shapeTypes` so casing can't make a
 * populated deck look empty again. See shapeTypes.ts.
 */
const isTextishType = canHoldText;

/**
 * Descend into GROUP shapes and return the flat list of leaf (non-group)
 * shapes, each tagged with its slide id. Designed templates wrap their real
 * text in groups; reading only top-level shapes makes that text invisible and
 * a populated deck look blank (post-mortem #1). Depth-capped to avoid runaway
 * nesting. Input shapes must already have items/id,name,type loaded.
 */
async function expandGroups(
  ctx: PowerPoint.RequestContext,
  seed: { sid: string; sh: PowerPoint.Shape }[],
  maxDepth = 3
): Promise<{ sid: string; sh: PowerPoint.Shape; grouped: boolean }[]> {
  const leaves: { sid: string; sh: PowerPoint.Shape; grouped: boolean }[] = [];
  let frontier = seed.map((e) => ({ ...e, grouped: false }));
  for (let d = 0; d <= maxDepth; d++) {
    const groups = frontier.filter((e) => isGroupType(e.sh.type));
    for (const e of frontier) {
      if (!isGroupType(e.sh.type)) leaves.push(e);
    }
    if (groups.length === 0) break;
    for (const g of groups) {
      try {
        // Geometry too: read_slide reports x/y/w/h for grouped children, and a
        // group child whose `left` was never loaded throws on access — which made
        // read_slide fail outright on any slide holding a composite diagram (they
        // are all groups). That is precisely the slide the agent most needs to read.
        (g.sh as any).group.shapes.load(
          "items/id,items/name,items/type,items/left,items/top,items/width,items/height"
        );
      } catch {
        /* not a real group on this build */
      }
    }
    // eslint-disable-next-line no-await-in-loop
    await ctx.sync().catch(() => {});
    const next: { sid: string; sh: PowerPoint.Shape; grouped: boolean }[] = [];
    for (const g of groups) {
      try {
        for (const c of (g.sh as any).group.shapes.items) next.push({ sid: g.sid, sh: c, grouped: true });
      } catch {
        /* skip unreadable group */
      }
    }
    frontier = next;
  }
  return leaves;
}

export interface ShapeText {
  text: string;
  /** hasText true but text "" ⇒ the shape HAS content we could not read
   * (the silent-blank failure) — surfaced so the agent never assumes empty. */
  readable: boolean;
  hasText: boolean;
}

/**
 * Load text + hasText for many shapes, keyed "slideId:shapeId", tolerating
 * shapes whose text frame fails to load. Batch first; on a poisoned sync,
 * isolate shape-by-shape. hasText lets callers distinguish genuinely-empty
 * (hasText false) from unreadable (hasText true but text "").
 */
async function loadShapeTexts(
  ctx: PowerPoint.RequestContext,
  entries: { sid: string; sh: PowerPoint.Shape }[]
): Promise<Record<string, ShapeText>> {
  const out: Record<string, ShapeText> = {};
  if (entries.length === 0) return out;
  const readOne = (sh: PowerPoint.Shape) => {
    const tf = sh.textFrame;
    const tr = tf.textRange;
    tf.load("hasText");
    tr.load("text");
    return { tf, tr };
  };
  const record = (sid: string, sh: PowerPoint.Shape, tf: any, tr: any) => {
    let text = "";
    let hasText = false;
    try {
      text = tr.text || "";
    } catch {
      /* text unreadable */
    }
    try {
      hasText = !!tf.hasText;
    } catch {
      hasText = false;
    }
    out[`${sid}:${sh.id}`] = { text, hasText, readable: !(hasText && text === "") };
  };
  const queued: { sid: string; sh: PowerPoint.Shape; tf: any; tr: any }[] = [];
  for (const { sid, sh } of entries) {
    try {
      queued.push({ sid, sh, ...readOne(sh) });
    } catch {
      /* shape exposes no text frame on this host — not text, not unreadable */
    }
  }
  if (queued.length === 0) return out;
  try {
    await ctx.sync();
    for (const q of queued) record(q.sid, q.sh, q.tf, q.tr);
    return out;
  } catch {
    /* batch poisoned — isolate below */
  }
  for (const { sid, sh } of entries) {
    try {
      const { tf, tr } = readOne(sh);
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      record(sid, sh, tf, tr);
    } catch {
      /* this shape has no readable text */
    }
  }
  return out;
}

const CONTEXT_SLIDE_CAP = 60;

/** Read the current deck context (title, slide outline, selection) and post it to the iframe. */
async function sendPresentationContext(): Promise<void> {
  try {
    const payload = await PowerPoint.run(async (ctx) => {
      const pres = ctx.presentation;
      pres.load("title");
      const slides = pres.slides;
      slides.load("items/id");
      await ctx.sync();

      const total = slides.items.length;
      if (originalSlideIds === null) {
        originalSlideIds = new Set(slides.items.map((s) => s.id));
      }
      const windowItems = slides.items.slice(0, CONTEXT_SLIDE_CAP);

      // One pass to load shape names/types per slide in the window; a second
      // pass reads only title-ish placeholder text so the outline stays cheap.
      for (const s of windowItems) {
        s.shapes.load("items/id,items/name,items/type");
        s.layout.load("name");
      }
      await ctx.sync();

      const titleEntries: { sid: string; sh: PowerPoint.Shape }[] = [];
      const titleShapeIdBySlide: Record<string, string> = {};
      for (const s of windowItems) {
        const t = s.shapes.items.find(
          (sh) => /title/i.test(sh.name) && isTextishType(sh.type)
        );
        if (t) {
          titleEntries.push({ sid: s.id, sh: t });
          titleShapeIdBySlide[s.id] = t.id;
        }
      }
      const titleTexts = await loadShapeTexts(ctx, titleEntries);

      let activeIndex: number | null = null;
      let selIds: string[] = [];
      if (api.v15) {
        try {
          const selSlides = pres.getSelectedSlides();
          selSlides.load("items/id,items/index");
          await ctx.sync();
          selIds = selSlides.items.map((s) => s.id);
          activeIndex = selSlides.items.length ? selSlides.items[0].index + 1 : null;
        } catch {
          /* selection is best-effort */
        }
      }

      const titles: Record<number, string> = {};
      windowItems.forEach((s, i) => {
        const shid = titleShapeIdBySlide[s.id];
        if (shid !== undefined) titles[i] = (titleTexts[`${s.id}:${shid}`]?.text || "").slice(0, 120);
      });

      return {
        title: pres.title,
        slideCount: total,
        activeSlideIndex: activeIndex,
        dims: { widthPt: 960, heightPt: 540 },
        slides: windowItems.map((s, i) => ({
          id: s.id,
          index: i + 1,
          title: titles[i] ?? "",
          layout: s.layout?.name ?? "",
          shapeCount: s.shapes.items.length,
        })),
        selection: { slideIds: selIds },
        apiLevel: api.v110 ? "1.10" : api.v19 ? "1.9" : api.v18 ? "1.8" : "<1.8",
      };
    });
    notifyIframe({ type: "@office-plugin", action: "ppt_context", presentation: payload });
  } catch (err: any) {
    console.warn("presentation context failed:", err?.message ?? err);
  }
}

/* ── Slide/shape resolution helpers (shared by read + write handlers) ─────── */

/** Resolve a slide reference (id string or 1-based index) to a Slide. Throws if not found. */
async function resolveSlide(ctx: PowerPoint.RequestContext, ref: unknown): Promise<PowerPoint.Slide> {
  const slides = ctx.presentation.slides;
  slides.load("items/id");
  await ctx.sync();
  const asString = String(ref);
  const byId = slides.items.find((s) => s.id === asString);
  if (byId) return byId;
  const idx = Number(asString);
  if (Number.isInteger(idx) && idx >= 1 && idx <= slides.items.length) return slides.items[idx - 1];
  throw new Error(`slide '${asString}' not found (use an id from inspect_deck or a 1-based index)`);
}

/** Resolve a shape on a slide by id or name. Throws if not found. */
async function resolveShape(
  ctx: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  ref: unknown
): Promise<PowerPoint.Shape> {
  slide.shapes.load("items/id,items/name");
  await ctx.sync();
  const asString = String(ref);
  const found =
    slide.shapes.items.find((sh) => sh.id === asString) ??
    slide.shapes.items.find((sh) => sh.name === asString);
  if (!found) throw new Error(`shape '${asString}' not found on the slide (use ids/names from read_slide)`);
  return found;
}

/* ── Read handlers ────────────────────────────────────────────────────────── */

const INSPECT_PAGE_CAP = 15;

/**
 * The firm's branded LAYOUT CATALOG: every slide master → its layouts → the
 * placeholders each layout provides. This is how the agent works WITHIN a
 * template — it adds slides from these named layouts (born branded: correct
 * background, fonts, colors, logo) and fills the placeholders, instead of
 * hand-rebuilding the look. Placeholder `name` is what set_text targets.
 * Best-effort: returns [] if the master model can't be read.
 */
async function readLayouts(ctx: PowerPoint.RequestContext): Promise<any[]> {
  try {
    const masters = ctx.presentation.slideMasters;
    masters.load(
      "items/id,items/name," +
        "items/layouts/items/id,items/layouts/items/name," +
        "items/layouts/items/shapes/items/name,items/layouts/items/shapes/items/type," +
        "items/layouts/items/shapes/items/left,items/layouts/items/shapes/items/top," +
        "items/layouts/items/shapes/items/width,items/layouts/items/shapes/items/height"
    );
    await ctx.sync();
    return masters.items.map((m) => ({
      id: m.id,
      name: m.name,
      layouts: m.layouts.items.map((l) => ({
        id: l.id,
        name: l.name,
        placeholders: l.shapes.items
          .filter((sh) => isPlaceholderType(sh.type))
          .map((sh) => ({
            name: sh.name,
            x: Math.round(sh.left),
            y: Math.round(sh.top),
            w: Math.round(sh.width),
            h: Math.round(sh.height),
          })),
        decor: l.shapes.items
          .filter((sh) => !isPlaceholderType(sh.type))
          .map((sh) => ({
            x: Math.round(sh.left),
            y: Math.round(sh.top),
            w: Math.round(sh.width),
            h: Math.round(sh.height),
          })),
      })),
    }));
  } catch {
    return [];
  }
}

async function handleInspectDeck(msg: any): Promise<void> {
  const out = await PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    const total = slides.items.length;
    const from = Math.max(1, Number(msg.from) || 1);
    const to = Math.min(total, Math.min(Number(msg.to) || from + INSPECT_PAGE_CAP - 1, from + INSPECT_PAGE_CAP - 1));

    const windowItems = slides.items.slice(from - 1, to);
    for (const s of windowItems) {
      s.layout.load("name");
      s.shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
    }
    await ctx.sync();

    // Second pass: text for text-capable shapes, DESCENDING INTO GROUPS
    // (templates wrap real text in groups — reading top-level only makes the
    // deck look blank, post-mortem #1). Fault-isolated.
    const seed: { sid: string; sh: PowerPoint.Shape }[] = [];
    for (const s of windowItems) for (const sh of s.shapes.items) seed.push({ sid: s.id, sh });
    const leaves = await expandGroups(ctx, seed);
    const textLeaves = leaves.filter((e) => isTextishType(e.sh.type));
    const fullTexts = await loadShapeTexts(ctx, textLeaves);

    // Aggregate signal: shapes that HAVE text we could not read. If this is
    // high across the whole window, the deck is not blank — reads are failing.
    let unreadable = 0;
    let readableWithText = 0;
    for (const v of Object.values(fullTexts)) {
      if (!v.readable) unreadable++;
      else if (v.text) readableWithText++;
    }

    // Extra grouped text leaves become their own shape entries so grouped
    // content is visible to the agent, not just top-level shapes.
    const groupedByslide: Record<string, any[]> = {};
    for (const e of leaves) {
      if (!e.grouped) continue;
      const st = fullTexts[`${e.sid}:${e.sh.id}`];
      if (!st) continue;
      (groupedByslide[e.sid] ??= []).push({
        id: e.sh.id,
        name: e.sh.name,
        type: e.sh.type,
        grouped: true,
        textPreview: (st.text || "").slice(0, 80),
        text_readable: st.readable,
      });
    }

    // The branded layout catalog — included on the first page (it is deck-wide,
    // so no need to repeat it on paged inspects). The agent uses it to add
    // slides from the firm's own layouts and stay on-brand.
    const layouts = msg.include_layouts === false || from > 1 ? undefined : await readLayouts(ctx);

    // Derive a content-safe rectangle from the master's decorative shapes: a wide,
    // short shape high on the slide is a header rule; anything low is a footer/logo.
    // Fall back to the fixed safe band when the master model is unreadable.
    let safeTop = SAFE_TOP;
    let safeBottom = SAFE_BOTTOM;
    if (Array.isArray(layouts)) {
      for (const m of layouts) {
        for (const l of m.layouts || []) {
          for (const d of l.decor || []) {
            const bottom = d.y + d.h;
            if (d.y < 120 && d.w > 400 && bottom + 8 > safeTop) safeTop = Math.min(150, bottom + 8);
            if (bottom > 470 && d.y - 8 < safeBottom) safeBottom = Math.max(440, d.y - 8);
          }
        }
      }
    }
    const content_safe_rect = { x: 40, y: safeTop, w: 880, h: Math.max(120, safeBottom - safeTop) };

    return {
      dims: { widthPt: 960, heightPt: 540 },
      content_safe_rect,
      pane_build: PANE_BUILD,
      slideCount: total,
      text_unreadable_shapes: unreadable,
      text_readable_shapes_with_content: readableWithText,
      layouts,
      slides: windowItems.map((s, i) => {
        const titleShape = s.shapes.items.find((sh) => /title/i.test(sh.name));
        const titleSt = titleShape ? fullTexts[`${s.id}:${titleShape.id}`] : undefined;
        return {
          id: s.id,
          index: from + i,
          title: (titleSt?.text || "").slice(0, 80),
          layout: s.layout?.name ?? "",
          shapes: s.shapes.items
            .map((sh) => {
              const st = fullTexts[`${s.id}:${sh.id}`];
              return {
                id: sh.id,
                name: sh.name,
                type: sh.type,
                textPreview: (st?.text || "").slice(0, 80),
                text_readable: st ? st.readable : undefined,
                x: Math.round(sh.left),
                y: Math.round(sh.top),
                w: Math.round(sh.width),
                h: Math.round(sh.height),
              };
            })
            .concat(groupedByslide[s.id] ?? []),
        };
      }),
    };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "inspect_deck_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleReadSlide(msg: any): Promise<void> {
  const out = await PowerPoint.run(async (ctx) => {
    const slide = await resolveSlide(ctx, msg.slide);
    slide.load("id");
    slide.layout.load("name");
    slide.shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    const index = slides.items.findIndex((s) => s.id === slide.id) + 1;

    // Text via the fault-isolated loader, DESCENDING INTO GROUPS (templates
    // wrap real text in groups; top-level-only reads make a full deck look
    // blank, post-mortem #1). Tables read separately below.
    const seed: { sid: string; sh: PowerPoint.Shape }[] = slide.shapes.items.map((sh) => ({ sid: slide.id, sh }));
    const leaves = await expandGroups(ctx, seed);
    const textLeaves = leaves.filter((e) => isTextishType(e.sh.type));
    const loaded = await loadShapeTexts(ctx, textLeaves);
    const texts: Record<string, ShapeText> = {};
    for (const [k, v] of Object.entries(loaded)) texts[k.slice(String(slide.id).length + 1)] = v;

    // Font size + autofit per text shape — the agent budgets text against box
    // geometry, and autofit ("shrink to fit") means an over-large nominal size
    // is NOT a real overflow (post-mortem #5).
    const fontSizes: Record<string, number> = {};
    const autofit: Record<string, string> = {};
    try {
      const reads = textLeaves.map(({ sh }) => {
        const f = sh.textFrame.textRange.font;
        f.load("size");
        const tf = sh.textFrame;
        tf.load("autoSizeSetting");
        return { sh, f, tf };
      });
      await ctx.sync();
      for (const { sh, f, tf } of reads) {
        try {
          if (typeof f.size === "number" && f.size > 0) fontSizes[sh.id] = f.size;
        } catch {
          /* mixed formatting returns null — skip */
        }
        try {
          if (tf.autoSizeSetting) autofit[sh.id] = String(tf.autoSizeSetting);
        } catch {
          /* skip */
        }
      }
    } catch {
      /* sizes are a hint, not a requirement */
    }

    // Fill + font colour per shape. Without these the agent cannot match an
    // existing deck's design: it sees geometry and text but no colour at all, so
    // it guesses fills from shape names and paints over a branded deck.
    // get_theme only yields a deck-wide palette with no attribution to shapes.
    const fills: Record<string, string> = {};
    const fillTypes: Record<string, string> = {};
    const fontColors: Record<string, string> = {};
    const asHex = (c: unknown): string | undefined => {
      const s = String(c ?? "");
      return /^#?[0-9a-fA-F]{6}$/.test(s) ? s.replace(/^#/, "").toUpperCase() : undefined;
    };
    try {
      const fillReads: { sh: PowerPoint.Shape; f: any }[] = [];
      for (const { sh } of leaves) {
        try {
          const f = sh.fill;
          f.load("foregroundColor,type");
          fillReads.push({ sh, f });
        } catch {
          /* shape exposes no fill on this host */
        }
      }
      const colorReads: { sh: PowerPoint.Shape; f: any }[] = [];
      for (const { sh } of textLeaves) {
        try {
          const f = sh.textFrame.textRange.font;
          f.load("color");
          colorReads.push({ sh, f });
        } catch {
          /* no text frame */
        }
      }
      await ctx.sync();
      for (const { sh, f } of fillReads) {
        try {
          const h = asHex(f.foregroundColor);
          if (h) fills[sh.id] = h;
        } catch {
          /* mixed / gradient fill returns null */
        }
        try {
          if (f.type) fillTypes[sh.id] = String(f.type);
        } catch {
          /* skip */
        }
      }
      for (const { sh, f } of colorReads) {
        try {
          const h = asHex(f.color);
          if (h) fontColors[sh.id] = h;
        } catch {
          /* mixed formatting returns null */
        }
      }
    } catch {
      /* colours are best-effort, never fatal */
    }

    // Per-PARAGRAPH styling for text shapes — so the agent can SEE a mixed box's
    // style logic (a bold brand-blue header line above regular black body) and
    // reproduce it on new content via set_text `segments`. The shape-level
    // font_color/fontSize above go null on a mixed box; this fills that gap.
    // Best-effort (getSubstring); only emitted for multi-paragraph boxes.
    const paragraphs: Record<
      string,
      Array<{ text: string; color?: string; bold?: boolean; italic?: boolean; size?: number }>
    > = {};
    try {
      const paraReads: { id: string; entries: { text: string; f: any }[] }[] = [];
      for (const { sh } of textLeaves) {
        const full = texts[sh.id]?.text ?? "";
        const trAny = sh.textFrame.textRange as any;
        if (!full || typeof trAny.getSubstring !== "function") continue;
        const sep = full.includes("\r") ? "\r" : "\n";
        const parts = full.split(sep);
        if (parts.length < 2) continue; // single paragraph — shape-level font covers it
        let off = 0;
        const entries: { text: string; f: any }[] = [];
        for (const part of parts) {
          if (part.length > 0) {
            try {
              const f = trAny.getSubstring(off, part.length).font;
              f.load("color,bold,italic,size");
              entries.push({ text: part, f });
            } catch {
              /* skip this paragraph */
            }
          }
          off += part.length + 1; // +1 for the paragraph separator
        }
        if (entries.length) paraReads.push({ id: sh.id, entries });
      }
      await ctx.sync();
      for (const pr of paraReads) {
        const arr: Array<{ text: string; color?: string; bold?: boolean; italic?: boolean; size?: number }> = [];
        for (const e of pr.entries) {
          const item: { text: string; color?: string; bold?: boolean; italic?: boolean; size?: number } = {
            text: e.text,
          };
          try {
            const h = asHex(e.f.color);
            if (h) item.color = h;
          } catch {
            /* mixed within the paragraph */
          }
          try {
            if (typeof e.f.bold === "boolean") item.bold = e.f.bold;
          } catch {
            /* skip */
          }
          try {
            if (typeof e.f.italic === "boolean") item.italic = e.f.italic;
          } catch {
            /* skip */
          }
          try {
            if (typeof e.f.size === "number" && e.f.size > 0) item.size = e.f.size;
          } catch {
            /* skip */
          }
          arr.push(item);
        }
        if (arr.length) paragraphs[pr.id] = arr;
      }
    } catch {
      /* per-paragraph styling is best-effort */
    }

    const tables: Record<string, any> = {};
    if (api.v18) {
      for (const sh of slide.shapes.items) {
        if (!isTableType(sh.type)) continue;
        try {
          const tbl = sh.getTable();
          tbl.load("values,rowCount,columnCount");
          // eslint-disable-next-line no-await-in-loop
          await ctx.sync();
          // `rows` mirrors add_table's `values` input naming so the agent can
          // read back exactly what it wrote and verify cell content (#5).
          tables[sh.id] = {
            rowCount: tbl.rowCount,
            colCount: tbl.columnCount,
            rows: tbl.values,
            values: tbl.values,
          };
        } catch {
          /* table read is best-effort */
        }
      }
    }

    // Composite tags (PF_KIND / PF_MODEL) on group shapes — this is what makes
    // step-plan clone-and-delta work: read the model off a duplicated diagram,
    // mutate it, and re-render. Read per-group so one bad group can't blank all.
    const composites: Record<string, { kind: string; model: any }> = {};
    for (const sh of slide.shapes.items) {
      if (!isGroupType(sh.type)) continue;
      try {
        const kindTag = sh.tags.getItemOrNullObject("PF_KIND");
        const modelTag = sh.tags.getItemOrNullObject("PF_MODEL");
        kindTag.load("value,isNullObject");
        modelTag.load("value,isNullObject");
        // eslint-disable-next-line no-await-in-loop
        await ctx.sync();
        if (!kindTag.isNullObject && !modelTag.isNullObject) {
          let model: any = null;
          try {
            model = JSON.parse(modelTag.value);
          } catch {
            model = null;
          }
          composites[sh.id] = { kind: String(kindTag.value).toLowerCase(), model };
        }
      } catch {
        /* tags are best-effort */
      }
    }

    let unreadable = 0;
    let readableWithText = 0;
    for (const v of Object.values(texts)) {
      if (!v.readable) unreadable++;
      else if (v.text) readableWithText++;
    }

    // Never let one unreadable property throw and blank the whole slide read.
    const num = (read: () => number): number | undefined => {
      try {
        const v = read();
        return typeof v === "number" ? Math.round(v) : undefined;
      } catch {
        return undefined;
      }
    };

    const shapeOut = (sh: PowerPoint.Shape, grouped: boolean) => {
      const fontSize = fontSizes[sh.id];
      const st = texts[sh.id];
      const isText = isTextishType(sh.type);
      return {
        id: sh.id,
        name: sh.name,
        type: sh.type,
        grouped: grouped || undefined,
        x: num(() => sh.left),
        y: num(() => sh.top),
        w: num(() => sh.width),
        h: num(() => sh.height),
        text: st?.text,
        text_readable: st ? st.readable : undefined,
        // Hex, no leading '#', ready to hand straight back to apply_ops set_style.
        // Absent means the fill is a gradient/picture/mixed, or unreadable.
        fill: fills[sh.id],
        fill_type: fillTypes[sh.id],
        font_color: fontColors[sh.id],
        // Per-paragraph styling for a MIXED box (header line vs body): each entry
        // { text, color, bold, italic, size }. Read the logic here, reproduce it
        // on new content with set_text `segments`. Absent on single-style boxes.
        paragraphs: paragraphs[sh.id],
        fontSize,
        autofit: autofit[sh.id],
        // Approximate text budget for this box at its current font size —
        // write MORE than this and it overflows or autofit-shrinks.
        fits_chars_approx: isText
          ? num(() => estimateTextFit("", sh.width, sh.height, fontSize ?? 18).approxCharsFit)
          : undefined,
        table: tables[sh.id],
        // Present on composite groups: { kind, model } — mutate and re-issue
        // the composite op to update the diagram (delete this group first).
        composite: composites[sh.id],
      };
    };

    const topShapes = slide.shapes.items.map((sh) => shapeOut(sh, false));
    const groupedShapes = leaves.filter((e) => e.grouped).map((e) => shapeOut(e.sh, true));

    return {
      id: slide.id,
      index,
      layout: slide.layout?.name ?? "",
      text_unreadable_shapes: unreadable,
      text_readable_shapes_with_content: readableWithText,
      shapes: topShapes.concat(groupedShapes),
    };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "read_slide_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleGetSelection(msg: any): Promise<void> {
  const out = await PowerPoint.run(async (ctx) => {
    if (!api.v15) return { slides: [], shapes: [], text: null };
    const pres = ctx.presentation;
    const selSlides = pres.getSelectedSlides();
    selSlides.load("items/id,items/index");
    const selShapes = pres.getSelectedShapes();
    selShapes.load("items/id,items/name,items/type,items/parentSlide/id" as any);
    const selText = pres.getSelectedTextRangeOrNullObject();
    selText.load("text,isNullObject" as any);
    await ctx.sync();

    let text: any = null;
    try {
      if (!(selText as any).isNullObject) text = { text: (selText as any).text };
    } catch {
      /* no text selection */
    }
    return {
      slides: selSlides.items.map((s) => ({ id: s.id, index: s.index + 1 })),
      shapes: selShapes.items.map((sh) => ({
        slideId: (sh as any).parentSlide?.id,
        id: sh.id,
        name: sh.name,
        type: sh.type,
      })),
      text,
    };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "get_selection_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleFindInDeck(msg: any): Promise<void> {
  const q = String(msg.query ?? "").toLowerCase();
  const out = await PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();

    const matches: any[] = [];
    // Slide-by-slide to bound each sync (mirrors the Excel per-sheet loop).
    for (let i = 0; i < slides.items.length; i++) {
      const s = slides.items[i];
      s.shapes.load("items/id,items/name,items/type");
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();

      // Expand groups so grouped text (common in templates) IS searched (#1).
      // eslint-disable-next-line no-await-in-loop
      const leaves = await expandGroups(
        ctx,
        s.shapes.items.map((sh) => ({ sid: s.id, sh }))
      );
      const textEntries = leaves.filter((e) => isTextishType(e.sh.type));
      const tableShapes: PowerPoint.Shape[] = [];
      for (const e of leaves) if (api.v18 && isTableType(e.sh.type)) tableShapes.push(e.sh);
      // eslint-disable-next-line no-await-in-loop
      const texts = await loadShapeTexts(ctx, textEntries);
      for (const { sh } of textEntries) {
        const text = texts[`${s.id}:${sh.id}`]?.text || "";
        const at = text.toLowerCase().indexOf(q);
        if (q && at >= 0) {
          matches.push({
            slideId: s.id,
            slideIndex: i + 1,
            shapeId: sh.id,
            shapeName: sh.name,
            snippet: text.slice(Math.max(0, at - 40), at + q.length + 40),
          });
        }
      }

      // Table cells — search every cell value.
      for (const sh of tableShapes) {
        try {
          const tbl = sh.getTable();
          tbl.load("values");
          // eslint-disable-next-line no-await-in-loop
          await ctx.sync();
          const vals = (tbl.values as string[][]) || [];
          for (let rr = 0; rr < vals.length; rr++) {
            for (let cc = 0; cc < vals[rr].length; cc++) {
              const text = String(vals[rr][cc] ?? "");
              if (q && text.toLowerCase().indexOf(q) >= 0) {
                matches.push({
                  slideId: s.id,
                  slideIndex: i + 1,
                  shapeId: sh.id,
                  shapeName: sh.name,
                  cell: [rr + 1, cc + 1],
                  snippet: text,
                });
              }
            }
          }
        } catch {
          /* table read is best-effort */
        }
      }
    }
    return { matches };
  }).catch((e) => ({ error: String(e?.message ?? e), matches: [] }));
  notifyIframe({ type: "@office-plugin", action: "find_in_deck_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleGetSlideImage(msg: any): Promise<void> {
  // Post-mortem: getImageAsBase64 can hang right after a large insert (the
  // slide has never been rendered on screen). Race a local timeout so the
  // caller gets a diagnosable answer instead of a dead 120s dispatch timeout.
  if (!api.v18) {
    notifyIframe({
      type: "@office-plugin",
      action: "get_slide_image_result",
      request_id: msg.request_id,
      reply_pid: msg.reply_pid,
      error: "slide rendering needs PowerPointApi 1.8 (update Office)",
      reason: "api_unavailable",
    });
    return;
  }
  const work = PowerPoint.run(async (ctx) => {
    const slide = await resolveSlide(ctx, msg.slide);
    slide.load("id");
    await ctx.sync();
    // getImageAsBase64 stalls on a slide the host has never rendered — the
    // usual case right after an insert. Bring it into the view first so the
    // host has a rendered surface to hand back. Best-effort: if selection is
    // unavailable we still attempt the render and fall through to the timeout.
    if (api.v15) {
      try {
        ctx.presentation.setSelectedSlides([slide.id]);
        await ctx.sync();
      } catch {
        /* selection is a render hint, not a requirement */
      }
    }
    // Bound the payload. A slide rendered at width 600 can produce ~500KB of
    // base64, which (a) is chunked into ~22 relay messages and (b) lands in the
    // agent's context as TEXT, swamping the session even when it arrives. Render,
    // measure, and shrink until it fits the budget. Report the width actually used
    // so the agent knows it is not looking at what it asked for.
    let width = Math.min(MAX_IMAGE_WIDTH, Math.max(120, Number(msg.width) || DEFAULT_IMAGE_WIDTH));
    let b64 = "";
    let attempts = 0;
    for (;;) {
      const img = (slide as any).getImageAsBase64({ width });
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      b64 = img.value as string;
      attempts++;
      if (b64.length <= MAX_IMAGE_B64_BYTES || width <= 160 || attempts >= 4) break;
      // PNG size grows roughly with area, so scale the width by sqrt(ratio).
      const ratio = Math.sqrt(MAX_IMAGE_B64_BYTES / b64.length);
      width = Math.max(160, Math.floor(width * Math.min(0.85, ratio)));
    }
    if (b64.length > MAX_IMAGE_B64_BYTES) {
      return {
        error:
          `this slide renders to ${b64.length} bytes of base64 even at width ${width}, over the ` +
          `${MAX_IMAGE_B64_BYTES}-byte cap. Verify content with read_slide (text, fill, font_color) instead.`,
        reason: "image_too_large",
        bytes: b64.length,
        width_used: width,
      };
    }
    return { slideId: slide.id, png_base64: b64, bytes: b64.length, width_used: width };
  });
  // Fail FAST with a structured reason code. getImageAsBase64 is unreliable on
  // some desktop builds (notably Mac) and can hang; content verification
  // (read_slide text + `rows`, find_in_deck) is the reliable path. `timeout`
  // is an optional caller override in ms, clamped to [5000, 60000].
  const timeoutMs = Math.min(60000, Math.max(5000, Number(msg.timeout) || 25000));
  const out = await Promise.race([
    work,
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            error:
              "slide render did not return in time (getImageAsBase64 is unreliable on some desktop " +
              "builds). Do NOT keep retrying it or ask the user to reopen the pane. Verify content " +
              "another way: read_slide returns every shape's text (incl. grouped shapes) and each " +
              "table's `rows`, and find_in_deck searches all text including table cells.",
            reason: "render_timeout",
          }),
        timeoutMs
      )
    ),
  ]).catch((e: any) => ({ error: String(e?.message ?? e), reason: "render_error" }));
  notifyIframe({ type: "@office-plugin", action: "get_slide_image_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...(out as any) });
}

/* ── Write handler: apply_ops ─────────────────────────────────────────────── */

/** Alias map for the most common shape types; anything else passes through raw
 * to GeometricShapeType (~178 presets) and an unknown value lands in skipped. */
const SHAPE_ALIASES: Record<string, string> = {
  rect: "rectangle",
  rectangle: "rectangle",
  roundrect: "roundRectangle",
  round_rect: "roundRectangle",
  roundrectangle: "roundRectangle",
  ellipse: "ellipse",
  circle: "ellipse",
  oval: "ellipse",
  triangle: "triangle",
  diamond: "diamond",
  chevron: "chevron",
  hexagon: "hexagon",
  pentagon: "pentagon",
  rightarrow: "rightArrow",
  right_arrow: "rightArrow",
  arrow: "rightArrow",
  leftarrow: "leftArrow",
  uparrow: "upArrow",
  downarrow: "downArrow",
  star: "star5Points",
  cloud: "cloud",
  heart: "heart",
  plaque: "plaque",
  can: "can",
  cube: "cube",
};

function geometricType(t: string): string {
  const key = String(t).toLowerCase().replace(/[\s-]/g, "");
  return SHAPE_ALIASES[key] ?? String(t);
}

const toHex = (c: string): string => {
  const s = String(c).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

/** Strip a data-URI prefix — Office.js base64 image APIs want bare base64. */
function bareBase64(s: string): string {
  return String(s).replace(/^data:[^;]+;base64,/, "");
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Replace every occurrence, returning the new text and the match count. */
function replaceAllText(
  text: string,
  find: string,
  replace: string,
  matchCase: boolean
): { text: string; count: number } {
  if (!find) return { text, count: 0 };
  const re = new RegExp(escapeRegExp(find), matchCase ? "g" : "gi");
  let count = 0;
  const out = text.replace(re, () => {
    count++;
    return replace;
  });
  return { text: out, count };
}

/** Font size from an op, accepting `size` OR `fontSize` (the DSL's name — agents
 * reach for it out of habit, so honor both). Returns undefined if neither set. */
function fontSizeOf(o: any): number | undefined {
  const v = o.size ?? o.fontSize;
  return v == null ? undefined : Number(v);
}

function applyShapeStyle(shape: PowerPoint.Shape, o: any): void {
  if (o.fill) shape.fill.setSolidColor(toHex(o.fill));
  if (o.line_color || o.line_width !== undefined) {
    if (o.line_color) shape.lineFormat.color = toHex(o.line_color);
    if (o.line_width !== undefined) shape.lineFormat.weight = Number(o.line_width);
  }
  if (o.no_line) shape.lineFormat.visible = false;
  const font = shape.textFrame?.textRange?.font;
  if (font) {
    const size = fontSizeOf(o);
    if (o.font) font.name = String(o.font);
    if (size !== undefined && !Number.isNaN(size)) font.size = size;
    if (o.color) font.color = toHex(o.color);
    if (o.bold !== undefined) font.bold = !!o.bold;
    if (o.italic !== undefined) font.italic = !!o.italic;
  }
  if (o.align && shape.textFrame?.textRange) {
    try {
      shape.textFrame.textRange.paragraphFormat.horizontalAlignment = capAlign(o.align);
    } catch {
      /* alignment is best-effort */
    }
  }
  if (o.valign && shape.textFrame) {
    try {
      (shape.textFrame as any).verticalAlignment = capValign(o.valign);
    } catch {
      /* best-effort */
    }
  }
  if (o.autosize && shape.textFrame) {
    // Control PowerPoint's autofit: "none" (fixed box, no silent shrinking),
    // "shrink_text" (shrink to fit shape), "grow_shape" (shape grows to text).
    try {
      const m: Record<string, string> = {
        none: "None",
        shrink_text: "TextToFitShape",
        grow_shape: "ShapeToFitText",
      };
      const v = m[String(o.autosize).toLowerCase()];
      if (v) (shape.textFrame as any).autoSizeSetting = v;
    } catch {
      /* autosize is best-effort */
    }
  }
}

function capAlign(a: string): PowerPoint.ParagraphHorizontalAlignment {
  const m: Record<string, string> = { left: "Left", center: "Center", right: "Right", justify: "Justify" };
  return (m[String(a).toLowerCase()] ?? "Left") as PowerPoint.ParagraphHorizontalAlignment;
}

function capValign(a: string): string {
  const m: Record<string, string> = { top: "Top", middle: "Middle", bottom: "Bottom" };
  return m[String(a).toLowerCase()] ?? "Middle";
}

/** Create native shapes for one layout-engine primitive; returns created shapes. */
function createPrimitive(shapes: PowerPoint.ShapeCollection, p: Primitive): PowerPoint.Shape[] {
  const made: PowerPoint.Shape[] = [];
  if (p.kind === "line") {
    // addLine takes a BOUNDING BOX, not two endpoints. Two consequences, both of
    // which shipped broken structure charts:
    //  1. A zero-width or zero-height box is rejected and silently replaced with
    //     72pt (1 inch), so every axis-aligned segment became a 72pt box and the
    //     connector drew corner-to-corner — a diagonal. Clamp zero to EPS instead.
    //  2. A box cannot encode direction: an up-and-right segment renders as
    //     down-and-right. So never emit a diagonal — decompose it into an L of two
    //     axis-aligned segments, which are direction-agnostic.
    const EPS = 0.5;
    const dx = p.x2 - p.x1;
    const dy = p.y2 - p.y1;
    const diagonal = Math.abs(dx) > 0.01 && Math.abs(dy) > 0.01;
    const segments: { x1: number; y1: number; x2: number; y2: number }[] = diagonal
      ? [
          { x1: p.x1, y1: p.y1, x2: p.x2, y2: p.y1 },
          { x1: p.x2, y1: p.y1, x2: p.x2, y2: p.y2 },
        ]
      : [{ x1: p.x1, y1: p.y1, x2: p.x2, y2: p.y2 }];

    for (const s of segments) {
      const w = Math.abs(s.x2 - s.x1);
      const h = Math.abs(s.y2 - s.y1);
      if (w < 0.01 && h < 0.01) continue; // degenerate point — nothing to draw
      const line = shapes.addLine(PowerPoint.ConnectorType.straight as any, {
        left: Math.min(s.x1, s.x2),
        top: Math.min(s.y1, s.y2),
        width: w < 0.01 ? EPS : w,
        height: h < 0.01 ? EPS : h,
      });
      if (p.style.lineColor) line.lineFormat.color = toHex(p.style.lineColor);
      if (p.style.lineWeight) line.lineFormat.weight = p.style.lineWeight;
      if (p.dashed) {
        try {
          line.lineFormat.dashStyle = "Dash" as any;
        } catch {
          /* dash is best-effort */
        }
      }
      made.push(line);
    }
    if (p.label) {
      // Anchor the percentage on its own drop, immediately above the child it
      // enters — that is where a legal chart puts it, and it is unambiguous about
      // which edge it belongs to. Centring on the segment's midpoint bunched three
      // labels within 5pt of each other once each parent got its own bus height,
      // and a 60pt box over 37pt of spacing guaranteed they overlapped.
      const vertical = Math.abs(p.x2 - p.x1) < 0.01;
      const W = 48; // fits "33.33%" at 9pt on one line
      const left = (vertical ? p.x1 : (p.x1 + p.x2) / 2) - W / 2;
      const top = (vertical ? Math.max(p.y1, p.y2) - 20 : (p.y1 + p.y2) / 2 - 8) - 0;
      const lbl = shapes.addTextBox(p.label, { left: left, top: top, width: W, height: 14 });
      const f = lbl.textFrame.textRange.font;
      f.size = p.style.fontSize ?? 9;
      f.color = toHex(p.style.fontColor ?? "64748B");
      try {
        lbl.textFrame.textRange.paragraphFormat.horizontalAlignment = capAlign("center");
      } catch {
        /* best-effort */
      }
      // Never wrap a percentage: "33.33%" dropping its "%" to a second line was
      // the last visible nit. A single-line box also can't collide with a sibling.
      try {
        (lbl.textFrame as any).wordWrap = false;
      } catch {
        /* best-effort */
      }
      made.push(lbl);
    }
    return made;
  }
  if (p.kind === "text") {
    const t = shapes.addTextBox(p.text, { left: p.x, top: p.y, width: p.w, height: p.h });
    const f = t.textFrame.textRange.font;
    if (p.style.fontSize) f.size = p.style.fontSize;
    if (p.style.fontColor) f.color = toHex(p.style.fontColor);
    if (p.style.bold !== undefined) f.bold = p.style.bold;
    if (p.style.align) {
      try {
        t.textFrame.textRange.paragraphFormat.horizontalAlignment = capAlign(p.style.align);
      } catch {
        /* best-effort */
      }
    }
    made.push(t);
    return made;
  }
  // Box kinds: rect / roundRect / ellipse / chevron / parallelogram / hexagon /
  // diamond / triangle (structure-chart entity shapes + flow arrowheads).
  const typeMap: Record<string, string> = {
    rect: "rectangle",
    roundRect: "roundRectangle",
    ellipse: "ellipse",
    chevron: "chevron",
    parallelogram: "parallelogram",
    hexagon: "hexagon",
    diamond: "diamond",
    triangle: "triangle",
  };
  // A flow arrowhead (triangle + arrowDir) renders as a directional block arrow,
  // which needs no rotation and reads correctly on every host.
  const arrowGeom: Record<string, string> = {
    up: "upArrow",
    down: "downArrow",
    left: "leftArrow",
    right: "rightArrow",
  };
  const geomKey =
    p.kind === "triangle" && (p as any).arrowDir ? arrowGeom[(p as any).arrowDir] : typeMap[p.kind];
  const sh = shapes.addGeometricShape((geomKey ?? "rectangle") as any, {
    left: p.x,
    top: p.y,
    width: p.w,
    height: p.h,
  });
  if (p.style.fill) sh.fill.setSolidColor(toHex(p.style.fill));
  // A box carrying a border color keeps its outline (entity boxes); a plain
  // filled box hides it (composite backdrops). Dashed = disregarded/foreign.
  if (p.style.lineColor) {
    sh.lineFormat.color = toHex(p.style.lineColor);
    if (p.style.lineWeight) sh.lineFormat.weight = p.style.lineWeight;
    if (p.dashed) {
      try {
        sh.lineFormat.dashStyle = "Dash" as any;
      } catch {
        /* dash is best-effort */
      }
    }
  } else {
    sh.lineFormat.visible = false;
  }
  if (p.rotation !== undefined && api.v110) {
    try {
      (sh as any).rotation = p.rotation;
    } catch {
      /* rotation is 1.10; arrowhead still reads without it */
    }
  }
  const name = p.text ?? "";
  const label = p.sub ? `${name}\n${p.sub}` : name;
  if (label) {
    sh.textFrame.textRange.text = label;
    const f = sh.textFrame.textRange.font;
    f.size = p.style.fontSize ?? 11;
    f.color = toHex(p.style.fontColor ?? "FFFFFF");
    if (p.style.bold !== undefined) f.bold = p.style.bold;
    try {
      sh.textFrame.textRange.paragraphFormat.horizontalAlignment = capAlign(p.style.align ?? "center");
    } catch {
      /* best-effort */
    }
    // Breathing room inside the box, and vertical centring — text was sitting hard
    // against the outline on tight entity boxes.
    try {
      const tf = sh.textFrame as any;
      tf.leftMargin = 6;
      tf.rightMargin = 6;
      tf.topMargin = 3;
      tf.bottomMargin = 3;
      tf.verticalAlignment = "Middle";
    } catch {
      /* margins are best-effort on older builds */
    }
    // Entity name bold, formation/jurisdiction line italic and a step smaller — the
    // convention on legal structure charts, and it separates identity from detail.
    if (p.sub && name) {
      try {
        const nameRun = (sh.textFrame.textRange as any).getSubstring(0, name.length);
        nameRun.font.bold = true;
        const subRun = (sh.textFrame.textRange as any).getSubstring(name.length + 1, p.sub.length);
        subRun.font.bold = false;
        subRun.font.italic = true;
        subRun.font.size = Math.max(7, (p.style.fontSize ?? 11) - 1.5);
      } catch {
        /* getSubstring is not on every build — whole-range styling still applies */
      }
    }
    // Let PowerPoint shrink text that still would not fit. The layout engine sizes
    // the box and steps the font down, but it cannot know a slanted shape's real
    // internal text width — a parallelogram clipped "…· Joint" mid-word twice. This
    // makes clipping structurally impossible rather than a thing we keep estimating.
    try {
      sh.textFrame.autoSizeSetting = "AutoSizeTextToFitShape" as any;
    } catch {
      /* not on this build — the engine's own sizing still applies */
    }
    try {
      sh.textFrame.wordWrap = true;
    } catch {
      /* best-effort */
    }
  }
  made.push(sh);
  return made;
}

function regionFromOp(o: any): Region {
  if (o.x !== undefined && o.y !== undefined && o.w !== undefined && o.h !== undefined) {
    return { x: Number(o.x), y: Number(o.y), w: Number(o.w), h: Number(o.h) };
  }
  return DEFAULT_REGION;
}

// GENERIC fallback safe band for ANY branded master — NOT tuned to one firm.
// Branded masters reserve a top band (header rule / title chrome) and a bottom
// band (corner logo / footer); these are conservative defaults for a typical
// firm master, used only when the deck's own decor shapes can't be read (see
// content_safe_rect below, which is the accurate, per-deck path). Do not narrow
// these to any single deck's exact pixels — that would over-fit the fallback.
const SAFE_TOP = 128; // clears a header rule sitting ~1.4in down (typical)
const SAFE_BOTTOM = 490; // keeps content above a bottom-corner logo (typical)
function clampRegionToSafe(r: Region): Region {
  const top = Math.max(r.y, SAFE_TOP);
  const bottom = Math.min(r.y + r.h, SAFE_BOTTOM);
  return { x: r.x, y: top, w: r.w, h: Math.max(60, bottom - top) };
}

async function handleApplyOps(msg: any): Promise<void> {
  const norm = normalizeOps(msg.ops);
  if (norm.ok === false) {
    notifyIframe({
      type: "@office-plugin",
      action: "apply_ops_result",
      request_id: msg.request_id,
      reply_pid: msg.reply_pid,
      applied: 0,
      created: [],
      skipped: [],
      error: `op #${norm.index}: ${norm.message}`,
    });
    return;
  }

  // Pre-fetch any add_image URLs OUTSIDE PowerPoint.run (no awaiting foreign
  // promises inside a batch).
  const imageData: Record<number, string> = {};
  for (let i = 0; i < norm.ops.length; i++) {
    const o = norm.ops[i] as any;
    if (o.op === "add_image") {
      try {
        if (o.png_base64) imageData[i] = bareBase64(String(o.png_base64));
        else if (o.url) {
          // eslint-disable-next-line no-await-in-loop
          const resp = await fetch(String(o.url));
          // eslint-disable-next-line no-await-in-loop
          const buf = await resp.arrayBuffer();
          let bin = "";
          const bytes = new Uint8Array(buf);
          for (let b = 0; b < bytes.length; b++) bin += String.fromCharCode(bytes[b]);
          imageData[i] = window.btoa(bin);
        }
      } catch (e) {
        imageData[i] = "";
      }
    }
  }

  const result = await PowerPoint.run(async (ctx) => {
    let applied = 0;
    let replacedTotal = 0;
    const created: Array<{ op: string; index: number; slide_id?: string; shape_id?: string }> = [];
    const skipped: Array<{ op: string; index?: number; slide?: string; shape?: string; reason?: string }> = [];
    // Non-fatal advisories the agent should act on — e.g. a diagram too dense for
    // its region (split it / give it a full slide).
    const notes: Array<{ op: string; index: number; fit?: any; advice?: string }> = [];

    const pres = ctx.presentation;

    // An op may omit `slide` to target the most-recently-targeted slide
    // (current-slide model, seeded to the first selected slide or slide 1).
    let currentSlide: PowerPoint.Slide | null = null;
    if (api.v15) {
      try {
        const sel = pres.getSelectedSlides();
        sel.load("items/id");
        await ctx.sync();
        if (sel.items.length) currentSlide = sel.items[0];
      } catch {
        /* fall through to slide 1 */
      }
    }
    if (!currentSlide) {
      const slides = pres.slides;
      slides.load("items/id");
      await ctx.sync();
      currentSlide = slides.items[0] ?? null;
    }

    const targetSlide = async (o: any): Promise<PowerPoint.Slide> => {
      if (o.slide !== undefined && o.slide !== null) {
        currentSlide = await resolveSlide(ctx, o.slide);
      }
      if (!currentSlide) throw new Error("no slide to target — pass `slide` or add a slide first");
      return currentSlide;
    };

    // Each op is committed on its own sync, so (a) a slide/shape created earlier
    // in the batch exists before later ops target it, (b) one bad op is isolated
    // with its index + reason instead of failing the whole call, (c) clean ops land.
    for (let i = 0; i < norm.ops.length; i++) {
      const o = norm.ops[i] as any;
      const beforeApplied = applied;
      const beforeCreated = created.length;
      try {
        switch (o.op) {
          case "add_slide": {
            const slides = pres.slides;
            slides.load("items/id");
            await ctx.sync();
            const beforeIds = new Set(slides.items.map((s) => s.id));
            let addOptions: any;
            let layoutMatched = false;
            if (o.layout) {
              // Match the requested firm layout by name OR id across masters, so
              // the new slide is BORN branded (background/fonts/colors/logo from
              // the master). Fall back to a default add if not found.
              const masters = pres.slideMasters;
              masters.load("items/id,items/layouts/items/id,items/layouts/items/name");
              await ctx.sync();
              const want = String(o.layout).toLowerCase();
              outer: for (const m of masters.items) {
                for (const l of m.layouts.items) {
                  if (l.name.toLowerCase() === want || l.id === String(o.layout)) {
                    addOptions = { layoutId: l.id, slideMasterId: m.id };
                    layoutMatched = true;
                    break outer;
                  }
                }
              }
            }
            slides.add(addOptions);
            await ctx.sync();
            slides.load("items/id");
            await ctx.sync();
            const added = slides.items.find((s) => !beforeIds.has(s.id));
            if (added) {
              currentSlide = added;
              if (o.after !== undefined && api.v18) {
                const anchor = await resolveSlide(ctx, o.after);
                const all = pres.slides;
                all.load("items/id");
                await ctx.sync();
                const anchorIdx = all.items.findIndex((s) => s.id === anchor.id);
                (added as any).moveTo(anchorIdx + 1);
              }
              // Return the new slide's placeholders so the agent can fill the
              // branded layout by name (set_text into "Title 1" etc.) rather
              // than adding free text boxes that ignore the master's styling.
              let placeholders: any[] = [];
              try {
                added.shapes.load("items/name,items/type,items/left,items/top,items/width,items/height");
                await ctx.sync();
                placeholders = added.shapes.items
                  .filter((sh) => isPlaceholderType(sh.type))
                  .map((sh) => ({
                    name: sh.name,
                    x: Math.round(sh.left),
                    y: Math.round(sh.top),
                    w: Math.round(sh.width),
                    h: Math.round(sh.height),
                  }));
              } catch {
                /* placeholder read is best-effort */
              }
              const entry: any = { op: "add_slide", index: i, slide_id: added.id, placeholders };
              if (o.layout && !layoutMatched) {
                entry.warning = `layout "${o.layout}" not found in the deck's masters — added a default slide instead. Call inspect_deck to see the exact layout names available.`;
              }
              created.push(entry);
            }
            applied++;
            break;
          }
          case "delete_slide": {
            const s = await resolveSlide(ctx, o.slide);
            // Pre-existing slides (template/user content present when the pane
            // bound the deck) are protected: deleting one requires confirm:true,
            // i.e. the agent asked the user first. Agent-created slides delete freely.
            if (originalSlideIds?.has(s.id) && o.confirm !== true) {
              throw new Error(
                "this slide existed before this session (possibly the user's template or content). " +
                  "Ask the user before deleting it, then re-send this op with \"confirm\": true."
              );
            }
            if (currentSlide && currentSlide.id === s.id) currentSlide = null;
            s.delete();
            applied++;
            break;
          }
          case "move_slide": {
            if (!api.v18) throw new Error("move_slide needs PowerPointApi 1.8");
            const s = await resolveSlide(ctx, o.slide);
            (s as any).moveTo(Math.max(0, Number(o.index) - 1));
            applied++;
            break;
          }
          case "duplicate_slide": {
            if (!api.v18) throw new Error("duplicate_slide needs PowerPointApi 1.8");
            const s = await resolveSlide(ctx, o.slide);
            const coll = pres.slides;
            coll.load("items/id");
            s.load("id");
            const b64 = (s as any).exportAsBase64();
            await ctx.sync();
            const beforeIds = new Set(coll.items.map((x) => x.id));
            pres.insertSlidesFromBase64(b64.value, {
              formatting: "KeepSourceFormatting" as any,
              targetSlideId: `${s.id}#`,
            });
            await ctx.sync();
            coll.load("items/id");
            await ctx.sync();
            // Report the copy's id and make it the current slide, so follow-up
            // slide-less ops (the precedent refill: set_text / set_cell / ...)
            // land on the duplicate, not the original.
            const copy = coll.items.find((x) => !beforeIds.has(x.id));
            if (copy) {
              currentSlide = copy;
              created.push({ op: "duplicate_slide", index: i, slide_id: copy.id });
            }
            applied++;
            break;
          }
          case "apply_layout": {
            if (!api.v18) throw new Error("apply_layout needs PowerPointApi 1.8");
            const s = await resolveSlide(ctx, o.slide);
            const masters = pres.slideMasters;
            masters.load("items/layouts/items/id,items/layouts/items/name");
            await ctx.sync();
            let layout: PowerPoint.SlideLayout | null = null;
            for (const m of masters.items) {
              const l = m.layouts.items.find(
                (x) => x.name.toLowerCase() === String(o.layout).toLowerCase()
              );
              if (l) {
                layout = l;
                break;
              }
            }
            if (!layout) throw new Error(`layout '${o.layout}' not found`);
            (s as any).applyLayout(layout);
            applied++;
            break;
          }
          case "set_background": {
            if (!api.v110) throw new Error("set_background needs PowerPointApi 1.10");
            const s = await resolveSlide(ctx, o.slide);
            const bg = (s as any).background;
            if (o.color) bg.fill.setSolidColor(toHex(o.color));
            else if (o.image_base64) bg.fill.setImage(bareBase64(o.image_base64));
            applied++;
            break;
          }
          case "add_text_box": {
            const s = await targetSlide(o);
            const sh = s.shapes.addTextBox(String(o.text), {
              left: Number(o.x),
              top: Number(o.y),
              width: Number(o.w),
              height: Number(o.h),
            });
            applyShapeStyle(sh, o);
            sh.load("id");
            await ctx.sync();
            created.push({ op: "add_text_box", index: i, slide_id: s.id, shape_id: sh.id });
            const warn = overflowWarning(String(o.text), Number(o.w), Number(o.h), fontSizeOf(o) || 18);
            if (warn) {
              skipped.push({ op: "add_text_box", index: i, shape: sh.id, reason: warn });
            }
            applied++;
            break;
          }
          case "add_shape": {
            const s = await targetSlide(o);
            const sh = s.shapes.addGeometricShape(geometricType(o.shape_type) as any, {
              left: Number(o.x),
              top: Number(o.y),
              width: Number(o.w),
              height: Number(o.h),
            });
            if (o.text) sh.textFrame.textRange.text = String(o.text);
            applyShapeStyle(sh, o);
            sh.load("id");
            await ctx.sync();
            created.push({ op: "add_shape", index: i, slide_id: s.id, shape_id: sh.id });
            applied++;
            break;
          }
          case "add_line": {
            const s = await targetSlide(o);
            const connector =
              o.connector === "elbow"
                ? PowerPoint.ConnectorType.elbow
                : o.connector === "curve"
                ? PowerPoint.ConnectorType.curve
                : PowerPoint.ConnectorType.straight;
            const sh = s.shapes.addLine(connector as any, {
              left: Math.min(Number(o.x1), Number(o.x2)),
              top: Math.min(Number(o.y1), Number(o.y2)),
              width: Math.abs(Number(o.x2) - Number(o.x1)),
              height: Math.abs(Number(o.y2) - Number(o.y1)),
            });
            if (o.line_color) sh.lineFormat.color = toHex(o.line_color);
            if (o.line_width !== undefined) sh.lineFormat.weight = Number(o.line_width);
            sh.load("id");
            await ctx.sync();
            created.push({ op: "add_line", index: i, slide_id: s.id, shape_id: sh.id });
            applied++;
            break;
          }
          case "add_image": {
            const s = await targetSlide(o);
            const data = imageData[i];
            if (!data) throw new Error("image content unavailable (fetch failed or empty)");
            const box = { x: Number(o.x), y: Number(o.y), w: Number(o.w), h: Number(o.h) };
            const mode = (o.fit === "contain" || o.fit === "cover" || o.fit === "stretch" ? o.fit : "stretch") as FitMode;

            // stretch = existing behavior (no dims needed). contain/cover need the image's
            // natural size, so decode the base64 into an ImageBitmap and compute placement.
            let placeData = data;
            let place = box;
            if (mode !== "stretch") {
              const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
              const bmp = await createImageBitmap(new Blob([bytes]));
              try {
                const fit = computeImageFit(bmp.width, bmp.height, box, mode);
                place = fit.place;
                if (fit.crop) {
                  // cover: center-crop to the box aspect on a canvas, then fill the box.
                  // Render the crop at its FULL source resolution (not scaled to the
                  // box's point size) so a small placeholder frame stays crisp — the
                  // host downscales to the box, never up. Cap the longest side so a
                  // huge source doesn't blow up the data URL.
                  const CAP = 2000;
                  const cropScale = Math.min(1, CAP / Math.max(fit.crop.sw, fit.crop.sh));
                  const outW = Math.max(1, Math.round(fit.crop.sw * cropScale));
                  const outH = Math.max(1, Math.round(fit.crop.sh * cropScale));
                  const canvas = document.createElement("canvas");
                  canvas.width = outW;
                  canvas.height = outH;
                  const ctx = canvas.getContext("2d")!;
                  ctx.drawImage(bmp, fit.crop.sx, fit.crop.sy, fit.crop.sw, fit.crop.sh, 0, 0, outW, outH);
                  placeData = canvas.toDataURL("image/png").replace(/^data:[^;]+;base64,/, "");
                }
              } finally {
                bmp.close();
              }
            }

            const shapesAny = s.shapes as any;
            let sh: PowerPoint.Shape;
            if (typeof shapesAny.addPicture === "function") {
              sh = shapesAny.addPicture(placeData, { left: place.x, top: place.y, width: place.w, height: place.h });
            } else {
              if (!api.v18) throw new Error("add_image needs PowerPointApi 1.8 (picture fill)");
              sh = s.shapes.addGeometricShape("rectangle" as any, { left: place.x, top: place.y, width: place.w, height: place.h });
              (sh.fill as any).setImage(placeData);
              sh.lineFormat.visible = false;
            }
            sh.load("id");
            await ctx.sync();
            created.push({ op: "add_image", index: i, slide_id: s.id, shape_id: sh.id });
            applied++;
            break;
          }
          case "copy_shape": {
            // Copy ANY shape — including Freeform vector icons that Office.js
            // cannot clone natively (no copyTo/duplicate, no freeform-create) —
            // from a source slide onto a target slide by RASTERIZING it
            // (Shape.getImageAsBase64, PowerPointApi 1.10) and inserting the PNG
            // as a picture. This is how a firm's slide-master "icon library" gets
            // placed onto content slides. Raster, not vector: visually identical
            // for icons, but the placed copy is a picture, not an editable path.
            if (!api.v110) {
              throw new Error(
                "copy_shape needs PowerPointApi 1.10 (Shape.getImageAsBase64). Update Office, or " +
                  "copy the shape manually: select it, Ctrl+C, go to the target slide, Ctrl+V."
              );
            }
            const srcSlide = await resolveSlide(ctx, o.from_slide);
            const srcShape = await resolveShape(ctx, srcSlide, o.shape);
            srcShape.load("width,height");
            await ctx.sync();
            const srcW = Number(srcShape.width) || 1;
            const srcH = Number(srcShape.height) || 1;

            // The host rasterizes the vector at the requested pixel width, so a
            // larger width yields a sharper icon (not an upscaled bitmap). Base
            // it on the target box width, 2x for retina, clamped.
            const renderPx = Math.min(1200, Math.max(96, Math.round(Number(o.w) * (96 / 72) * 2)));
            // getImageAsBase64 can stall on a slide the host never brought into
            // view. Select the source slide first as a render hint (best-effort;
            // this changes host selection but not the `currentSlide` target var).
            if (api.v15) {
              try {
                pres.setSelectedSlides([srcSlide.id]);
                await ctx.sync();
              } catch {
                /* selection is a render hint, not a requirement */
              }
            }
            const img = (srcShape as any).getImageAsBase64({ width: renderPx });
            await ctx.sync();
            const b64 = bareBase64(String(img.value ?? ""));
            if (!b64) throw new Error("could not rasterize the source shape (getImageAsBase64 returned empty)");

            // Fit the icon inside the requested box PRESERVING aspect ratio
            // (centered) so a square icon in a non-square box is never squished.
            const boxX = Number(o.x);
            const boxY = Number(o.y);
            const boxW = Number(o.w);
            const boxH = Number(o.h);
            const scale = Math.min(boxW / srcW, boxH / srcH);
            const drawW = srcW * scale;
            const drawH = srcH * scale;
            const drawX = boxX + (boxW - drawW) / 2;
            const drawY = boxY + (boxH - drawH) / 2;

            const tgt = await targetSlide(o);
            const tgtShapesAny = tgt.shapes as any;
            let placed: PowerPoint.Shape;
            if (typeof tgtShapesAny.addPicture === "function") {
              placed = tgtShapesAny.addPicture(b64, { left: drawX, top: drawY, width: drawW, height: drawH });
            } else {
              placed = tgt.shapes.addGeometricShape("rectangle" as any, {
                left: drawX,
                top: drawY,
                width: drawW,
                height: drawH,
              });
              (placed.fill as any).setImage(b64);
              placed.lineFormat.visible = false;
            }
            placed.load("id");
            await ctx.sync();
            created.push({ op: "copy_shape", index: i, slide_id: tgt.id, shape_id: placed.id });
            applied++;
            break;
          }
          case "add_table": {
            if (!api.v18) throw new Error("add_table needs PowerPointApi 1.8");
            const s = await targetSlide(o);
            const parsed = parseTableValues(o.values);
            const values = parsed.text;
            const rows = values.length;
            const cols = values[0]?.length ?? 1;
            const opts: any = {
              left: Number(o.x),
              top: Number(o.y),
              width: Number(o.w),
              height: Number(o.h),
              values,
            };
            const hasHeader = o.header !== false;
            const anyCellStyle = parsed.styles.some((r) => r.some((st) => st));
            if (hasHeader || anyCellStyle) {
              opts.specificCellProperties = parsed.text.map((row, r) =>
                row.map((_cell, c) => {
                  const cp: any = {};
                  if (hasHeader && r === 0) {
                    cp.fill = { color: toHex(o.header_fill ?? "1F2937") };
                    cp.font = { bold: true, color: toHex(o.header_color ?? "FFFFFF") };
                  }
                  const st: CellStyle | undefined = parsed.styles[r]?.[c];
                  if (st) {
                    if (st.fill) cp.fill = { color: toHex(st.fill) };
                    if (st.color != null || st.bold != null) {
                      cp.font = {
                        ...(cp.font || {}),
                        ...(st.color != null ? { color: toHex(st.color) } : {}),
                        ...(st.bold != null ? { bold: st.bold } : {}),
                      };
                    }
                    if (st.align) cp.horizontalAlignment = capAlign(st.align);
                  }
                  return cp;
                })
              );
            }
            const sh = (s.shapes as any).addTable(rows, cols, opts);
            sh.load("id,height");
            await ctx.sync();
            // Best-effort per-column widths — Office.js table column-width control
            // varies by build; never fail the op if it is unavailable.
            if (Array.isArray(o.colW) && o.colW.length) {
              try {
                const tbl = (sh as any).table ?? sh;
                for (let c = 0; c < o.colW.length; c++) {
                  const col = tbl.columns?.getItemAt?.(c);
                  if (col && typeof o.colW[c] === "number") col.width = Number(o.colW[c]);
                }
                await ctx.sync();
              } catch {
                /* colW is best-effort */
              }
            }
            // The native table auto-grows rows to fit text; warn if it spilled
            // past the requested box (a common off-slide overflow).
            try {
              if (typeof sh.height === "number" && o.h && sh.height > Number(o.h) + 12) {
                skipped.push({
                  op: "add_table",
                  index: i,
                  shape: "(new table)",
                  reason: `NOTE (table auto-grew to ${Math.round(sh.height)}pt, past the requested ${Math.round(Number(o.h))}pt — long cell text is expanding rows; shorten cells or add a slide so it does not run off the slide).`,
                });
              }
            } catch {
              /* height read is best-effort */
            }
            created.push({ op: "add_table", index: i, slide_id: s.id, shape_id: sh.id });
            applied++;
            break;
          }
          case "set_text": {
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            let writtenText: string;
            if (Array.isArray(o.segments)) {
              // Styled authoring: write text run-by-run so headers keep their
              // (e.g. bold brand-blue) styling and body stays regular/black — the
              // agent reproduces a template's per-run style logic on NEW content,
              // even on a blank style deck with nothing to find/replace.
              const plan = planSegments(o.segments as TextSegment[]);
              writtenText = plan.fullText;
              const trr = sh.textFrame.textRange;
              trr.text = writtenText;
              await ctx.sync();
              const trAny = trr as any;
              if (typeof trAny.getSubstring === "function") {
                for (const run of plan.runs) {
                  try {
                    const sub = trAny.getSubstring(run.start, run.length);
                    const st = run.seg;
                    if (st.color != null) sub.font.color = toHex(String(st.color));
                    if (st.bold != null) sub.font.bold = !!st.bold;
                    if (st.italic != null) sub.font.italic = !!st.italic;
                    if (st.underline != null) sub.font.underline = (st.underline ? "Single" : "None") as any;
                    if (st.size != null) sub.font.size = Number(st.size);
                    if (st.font != null) sub.font.name = String(st.font);
                  } catch {
                    /* per-run styling is best-effort on this host */
                  }
                }
                await ctx.sync();
              }
            } else if (o.text !== undefined && o.text !== null) {
              writtenText = String(o.text);
              sh.textFrame.textRange.text = writtenText;
            } else {
              const tr = sh.textFrame.textRange;
              tr.load("text");
              await ctx.sync();
              const from = String(o.find);
              const to = String(o.replace);
              const full = tr.text || "";
              writtenText = from ? full.split(from).join(to) : full;
              // Replace each match IN PLACE via getSubstring so every run keeps its
              // own formatting. Template placeholders routinely pack a styled header
              // run (bold brand-blue "Background") and body runs (regular black) into
              // ONE box; setting the whole textRange.text collapses them to a single
              // font — the all-blue-bold failure. Walk matches RIGHT-TO-LEFT so the
              // earlier indices stay valid as replacements change length.
              const idxs: number[] = [];
              if (from) {
                let p = full.indexOf(from);
                while (p !== -1) {
                  idxs.push(p);
                  p = full.indexOf(from, p + from.length);
                }
              }
              const trAny = tr as any;
              if (idxs.length && typeof trAny.getSubstring === "function") {
                for (let k = idxs.length - 1; k >= 0; k--) {
                  trAny.getSubstring(idxs[k], from.length).text = to;
                }
                await ctx.sync();
              } else {
                // No match, or an older host without getSubstring: whole-text set
                // (this flattens mixed formatting — which is exactly why the agent
                // must prefer find/replace over a whole-placeholder set_text).
                tr.text = writtenText;
              }
            }
            // Fit check after the write. Autofit-aware (post-mortem #5): a box
            // set to shrink-to-fit will NOT clip over-large text, so a raw
            // line-count overflow at nominal size is a FALSE positive there.
            try {
              sh.load("width,height,type");
              const f = sh.textFrame.textRange.font;
              f.load("size");
              const tf = sh.textFrame;
              tf.load("autoSizeSetting");
              await ctx.sync();
              const size = typeof f.size === "number" && f.size > 0 ? f.size : 18;
              const shrinks = isShrinkToFit(tf.autoSizeSetting);
              const warn = overflowWarning(writtenText, sh.width, sh.height, size);
              if (warn && !shrinks && isPlaceholderType(sh.type)) {
                // A branded placeholder with autofit off overflows into the master
                // imagery. Try shrink-to-fit, then RE-READ to confirm it took —
                // a master-locked placeholder (e.g. a Title box) silently ignores
                // the override, so never report success we did not verify.
                let stuck = false;
                try {
                  tf.autoSizeSetting = "AutoSizeTextToFitShape" as any;
                  await ctx.sync();
                  tf.load("autoSizeSetting");
                  await ctx.sync();
                  stuck = isShrinkToFit(tf.autoSizeSetting);
                } catch {
                  /* older build or locked placeholder — treat as not stuck */
                }
                skipped.push({
                  op: "set_text",
                  index: i,
                  shape: String(o.shape),
                  reason: stuck
                    ? "NOTE (placeholder text overflowed; enabled shrink-to-fit so it stays inside the master's band — verify it did not become tiny; shorten if so)."
                    : "WARNING (placeholder text overflows and shrink-to-fit did NOT take — this placeholder locks its autofit, e.g. a Title box. SHORTEN the text, set a smaller explicit size, or move body content to a content placeholder / new slide; do not ship it bleeding).",
                });
              } else if (warn && !shrinks) {
                // Fixed non-placeholder box — real overflow/clip risk.
                skipped.push({ op: "set_text", index: i, shape: String(o.shape), reason: warn });
              } else if (warn && shrinks) {
                // Autofit will shrink to fit; flag only that it may end up small.
                skipped.push({
                  op: "set_text",
                  index: i,
                  shape: String(o.shape),
                  reason:
                    "NOTE (text written; the box autofits/shrinks so it will not clip, but this much text " +
                    "may render small — verify readability; shorten if it looks tiny).",
                });
              } else if (size < 11 && writtenText.length > 60 && !shrinks) {
                skipped.push({
                  op: "set_text",
                  index: i,
                  shape: String(o.shape),
                  reason: `WARNING (text written, but font is ${size}pt — unreadable). Cut the text or restructure; do not ship sub-11pt body text.`,
                });
              }
            } catch {
              /* fit check is best-effort */
            }
            // Stray-punctuation guard: a find/replace whose `find` omitted the
            // template's trailing '.' leaves a doubled period ("…days..") the user
            // then has to clean up. Flag it (never auto-edit their words) so the
            // agent re-does this box as a whole-text set_text.
            if (hasStrayDoublePunct(writtenText)) {
              skipped.push({
                op: "set_text",
                index: i,
                shape: String(o.shape),
                reason:
                  'WARNING (doubled punctuation in the result, e.g. ".." — a partial find/replace left the box\'s ' +
                  "original trailing punctuation behind). Re-send this box as a WHOLE-text set_text with the full clean string.",
              });
            }
            applied++;
            break;
          }
          case "set_style": {
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            const wantSize = fontSizeOf(o);
            // Forcing an explicit size must beat the template's autofit, which
            // otherwise re-expands/re-shrinks and the size silently reverts
            // (post-mortem #4). Pin autofit off when the caller sets a size.
            if (wantSize !== undefined && !Number.isNaN(wantSize) && o.autosize === undefined && sh.textFrame) {
              try {
                (sh.textFrame as any).autoSizeSetting = "None";
              } catch {
                /* best-effort */
              }
            }
            applyShapeStyle(sh, o);
            // Read the size back — if it did not take (inherited run formatting
            // overriding), tell the agent instead of a phantom applied:1 (#4).
            if (wantSize !== undefined && !Number.isNaN(wantSize)) {
              try {
                const f = sh.textFrame.textRange.font;
                f.load("size");
                await ctx.sync();
                if (typeof f.size === "number" && Math.abs(f.size - wantSize) > 0.5) {
                  skipped.push({
                    op: "set_style",
                    index: i,
                    shape: String(o.shape),
                    reason: `WARNING (font size did not apply — still ${f.size}pt, wanted ${wantSize}pt; the shape's runs may carry their own size). Re-send set_style with both "font" and "size" set together, which overrides run formatting.`,
                  });
                }
              } catch {
                /* read-back is best-effort */
              }
            }
            applied++;
            break;
          }
          case "transform": {
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            if (o.x !== undefined) sh.left = Number(o.x);
            if (o.y !== undefined) sh.top = Number(o.y);
            if (o.w !== undefined) sh.width = Number(o.w);
            if (o.h !== undefined) sh.height = Number(o.h);
            if (o.rotation !== undefined) {
              if (!api.v110) throw new Error("rotation needs PowerPointApi 1.10");
              (sh as any).rotation = Number(o.rotation);
            }
            applied++;
            break;
          }
          case "delete_shape": {
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            sh.delete();
            applied++;
            break;
          }
          case "group": {
            if (!api.v18) throw new Error("group needs PowerPointApi 1.8");
            const s = await targetSlide(o);
            const members: PowerPoint.Shape[] = [];
            for (const ref of o.shapes as any[]) {
              // eslint-disable-next-line no-await-in-loop
              members.push(await resolveShape(ctx, s, ref));
            }
            const g = (s.shapes as any).addGroup(members);
            g.load("id");
            await ctx.sync();
            created.push({ op: "group", index: i, slide_id: s.id, shape_id: g.id });
            applied++;
            break;
          }
          case "ungroup": {
            if (!api.v18) throw new Error("ungroup needs PowerPointApi 1.8");
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            (sh as any).group.ungroup();
            applied++;
            break;
          }
          case "z_order": {
            if (!api.v18) throw new Error("z_order needs PowerPointApi 1.8");
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            const m: Record<string, string> = {
              bringforward: "BringForward",
              bringtofront: "BringToFront",
              sendbackward: "SendBackward",
              sendtoback: "SendToBack",
            };
            const pos = m[String(o.position).toLowerCase().replace(/[\s_]/g, "")];
            if (!pos) throw new Error(`unknown z_order position '${o.position}'`);
            (sh as any).setZOrder(pos);
            applied++;
            break;
          }
          case "set_hyperlink": {
            if (!api.v110) throw new Error("set_hyperlink needs PowerPointApi 1.10");
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            (sh as any).setHyperlink({ address: String(o.url), screenTip: o.tooltip ? String(o.tooltip) : undefined });
            applied++;
            break;
          }
          case "set_cell": {
            if (!api.v18) throw new Error("set_cell needs PowerPointApi 1.8");
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            const tbl = sh.getTable() as any;
            const r0 = Number(o.row) - 1;
            const c0 = Number(o.col) - 1;
            const cell = tbl.getCellOrNullObject(r0, c0);
            // Verify the cell exists BEFORE writing — a null object (out-of-range
            // index, or the covered half of a merged cell) silently swallowed the
            // write while we still reported applied:1 (post-mortem #2/#8).
            cell.load("isNullObject");
            await ctx.sync();
            if (cell.isNullObject) {
              throw new Error(
                `no cell at row ${o.row}, col ${o.col} (table out of range, or it is the covered part of a merged cell)`
              );
            }
            if (o.text !== undefined) cell.text = String(o.text);
            if (api.v19) {
              // fill and font are navigation OBJECTS, not settable scalars —
              // assigning `cell.fill = {...}` throws "Attempted to assign to
              // readonly property" (post-mortem #2). Set through them instead.
              if (o.fill) {
                try {
                  cell.fill.setSolidColor(toHex(o.fill));
                } catch {
                  /* some builds gate cell fill; text still lands */
                }
              }
              const size = fontSizeOf(o);
              if (o.bold !== undefined) cell.font.bold = !!o.bold;
              if (o.color) cell.font.color = toHex(o.color);
              if (size !== undefined && !Number.isNaN(size)) cell.font.size = size;
              if (o.italic !== undefined) cell.font.italic = !!o.italic;
            }
            applied++;
            break;
          }
          case "merge_cells": {
            if (!api.v19) throw new Error("merge_cells needs PowerPointApi 1.9");
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            const tbl = sh.getTable() as any;
            const a = o.area;
            const [r1, c1, r2, c2] = Array.isArray(a) ? a.map(Number) : [a.from[0], a.from[1], a.to[0], a.to[1]].map(Number);
            tbl.mergeCells({ firstRow: r1 - 1, firstColumn: c1 - 1, lastRow: r2 - 1, lastColumn: c2 - 1 });
            applied++;
            break;
          }
          case "insert_rows":
          case "delete_rows":
          case "insert_cols":
          case "delete_cols": {
            if (!api.v19) throw new Error(`${o.op} needs PowerPointApi 1.9`);
            const s = await targetSlide(o);
            const sh = await resolveShape(ctx, s, o.shape);
            const tbl = sh.getTable() as any;
            const at = Number(o.at) - 1;
            const count = Number(o.count);
            if (o.op === "insert_rows") tbl.rows.add(at, count);
            else if (o.op === "delete_rows") tbl.rows.deleteRows?.(at, count) ?? tbl.deleteRows?.(at, count);
            else if (o.op === "insert_cols") tbl.columns.add(at, count);
            else tbl.columns.deleteColumns?.(at, count) ?? tbl.deleteColumns?.(at, count);
            applied++;
            break;
          }
          case "set_tags": {
            const s = await targetSlide(o);
            const target = o.shape ? await resolveShape(ctx, s, o.shape) : s;
            for (const [k, v] of Object.entries(o.tags as Record<string, unknown>)) {
              target.tags.add(String(k).toUpperCase(), String(v));
            }
            applied++;
            break;
          }
          case "replace_all": {
            // Deck-wide text sweep: every text-capable shape and (1.8+) table
            // cell on every slide. Case-sensitive unless match_case === false.
            const slides = pres.slides;
            slides.load("items/id");
            await ctx.sync();
            const find = String(o.find);
            const rep = String(o.replace);
            const matchCase = o.match_case !== false;
            for (const s of slides.items) {
              s.shapes.load("items/id,items/type");
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync();
              const textEntries: { sid: string; sh: PowerPoint.Shape }[] = [];
              const tbls: PowerPoint.Table[] = [];
              const groups: PowerPoint.Shape[] = [];
              for (const sh of s.shapes.items) {
                if (isTextishType(sh.type)) {
                  textEntries.push({ sid: s.id, sh });
                } else if (isGroupType(sh.type)) {
                  groups.push(sh);
                } else if (api.v18 && isTableType(sh.type)) {
                  try {
                    const tbl = sh.getTable();
                    tbl.load("values,rowCount,columnCount");
                    tbls.push(tbl);
                  } catch {
                    /* table read is best-effort */
                  }
                }
              }
              // One level into groups (composites, grouped precedent art) —
              // their member text is part of the deck too.
              for (const g of groups) {
                try {
                  (g as any).group.shapes.load("items/id,items/type");
                } catch {
                  /* group introspection is best-effort */
                }
              }
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
              for (const g of groups) {
                try {
                  for (const inner of (g as any).group.shapes.items) {
                    if (isTextishType(inner.type)) textEntries.push({ sid: s.id, sh: inner });
                  }
                } catch {
                  /* skip unreadable groups */
                }
              }
              // eslint-disable-next-line no-await-in-loop
              const loadedTexts = await loadShapeTexts(ctx, textEntries);
              for (const { sid, sh } of textEntries) {
                try {
                  const cur = loadedTexts[`${sid}:${sh.id}`]?.text;
                  if (cur === undefined) continue;
                  const r = replaceAllText(cur, find, rep, matchCase);
                  if (r.count > 0) {
                    sh.textFrame.textRange.text = r.text;
                    replacedTotal += r.count;
                  }
                } catch {
                  /* skip unreadable shapes */
                }
              }
              for (const tbl of tbls) {
                try {
                  const vals = tbl.values as string[][];
                  for (let rr = 0; rr < vals.length; rr++) {
                    for (let cc = 0; cc < vals[rr].length; cc++) {
                      const r = replaceAllText(String(vals[rr][cc] ?? ""), find, rep, matchCase);
                      if (r.count > 0) {
                        (tbl as any).getCellOrNullObject(rr, cc).text = r.text;
                        replacedTotal += r.count;
                      }
                    }
                  }
                } catch {
                  /* table cells are best-effort */
                }
              }
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
            }
            applied++;
            break;
          }
          case "stamp": {
            // Footer-style marking on EVERY slide (no footer API exists) —
            // Privileged & Confidential / Attorney Work Product / DRAFT.
            const slides = pres.slides;
            slides.load("items/id");
            await ctx.sync();
            const positions: Record<string, { x: number; y: number; w: number; align: string }> = {
              "bottom-left": { x: 24, y: 514, w: 420, align: "left" },
              "bottom-center": { x: 270, y: 514, w: 420, align: "center" },
              "bottom-right": { x: 516, y: 514, w: 420, align: "right" },
              "top-right": { x: 516, y: 8, w: 420, align: "right" },
            };
            const pos = positions[String(o.position)] ?? positions["bottom-left"];
            for (const s of slides.items) {
              const tb = s.shapes.addTextBox(String(o.text), {
                left: pos.x,
                top: pos.y,
                width: pos.w,
                height: 18,
              });
              tb.name = "PF Stamp";
              const f = tb.textFrame.textRange.font;
              f.size = o.size ? Number(o.size) : 9;
              f.color = toHex(o.color ?? "808080");
              if (o.bold !== undefined) f.bold = !!o.bold;
              try {
                tb.textFrame.textRange.paragraphFormat.horizontalAlignment = capAlign(pos.align);
              } catch {
                /* best-effort */
              }
            }
            await ctx.sync();
            applied++;
            break;
          }
          case "set_min_font": {
            // Deck-wide (or single-slide) READABILITY FLOOR: raise every text run
            // that renders below `size` up to it, WITHOUT shrinking runs already
            // large enough. This is the one-pass answer to "nothing smaller than
            // 20pt across the presentation" — otherwise the agent hunts box by box.
            const floor = Number(o.size ?? o.fontSize);
            const only = o.slide !== undefined ? String(o.slide) : null;
            const slides = pres.slides;
            slides.load("items/id");
            await ctx.sync();
            const targets = only
              ? slides.items.filter((s, idx) => String(s.id) === only || String(idx + 1) === only)
              : slides.items;
            let raisedRuns = 0;
            const raisedShapes = new Set<string>();
            const growSlides = new Set<string>();
            const shrinkShapes = new Set<string>();
            let mixedSkippedTotal = 0;
            for (const s of targets) {
              s.shapes.load("items/id,items/type");
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync();
              const leaves: PowerPoint.Shape[] = [];
              const groups: PowerPoint.Shape[] = [];
              for (const sh of s.shapes.items) {
                if (isTextishType(sh.type)) leaves.push(sh);
                else if (isGroupType(sh.type)) groups.push(sh);
              }
              for (const g of groups) {
                try {
                  (g as any).group.shapes.load("items/id,items/type");
                } catch {
                  /* group introspection is best-effort */
                }
              }
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
              for (const g of groups) {
                try {
                  for (const inner of (g as any).group.shapes.items) {
                    if (isTextishType(inner.type)) leaves.push(inner);
                  }
                } catch {
                  /* skip unreadable groups */
                }
              }
              // Pass 1: shape-level size + autofit + text (to split paragraphs).
              const reads = leaves.map((sh) => {
                const f = sh.textFrame.textRange.font;
                f.load("size");
                const tf = sh.textFrame;
                tf.load("autoSizeSetting");
                const tr = sh.textFrame.textRange;
                tr.load("text");
                return { sh, f, tf, tr };
              });
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
              // Pass 2: for boxes whose shape-level size is null (mixed runs),
              // load each paragraph's size so we raise only the small lines.
              const state = reads.map((r) => {
                const shapeSize: SizeRead = typeof r.f.size === "number" && r.f.size > 0 ? r.f.size : null;
                const autofit = String(r.tf.autoSizeSetting ?? "");
                let subs: { off: number; len: number; sf: any }[] = [];
                if (shapeSize === null) {
                  const full = r.tr.text || "";
                  const trAny = r.tr as any;
                  if (full && typeof trAny.getSubstring === "function") {
                    const sep = full.includes("\r") ? "\r" : "\n";
                    const parts = full.split(sep);
                    let off = 0;
                    for (const part of parts) {
                      if (part.length > 0) {
                        const sf = trAny.getSubstring(off, part.length).font;
                        sf.load("size");
                        subs.push({ off, len: part.length, sf });
                      }
                      off += part.length + 1;
                    }
                  }
                }
                return { r, shapeSize, autofit, subs };
              });
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
              // Pass 3: plan + apply per shape.
              for (const st of state) {
                try {
                  const paraSizes: SizeRead[] = st.subs.map((su) =>
                    typeof su.sf.size === "number" && su.sf.size > 0 ? su.sf.size : null
                  );
                  const plan = planFontFloor(st.shapeSize, paraSizes, floor);
                  if (!plan.actions.length) {
                    mixedSkippedTotal += plan.mixedSkipped.length;
                    continue;
                  }
                  for (const a of plan.actions) {
                    if (a.scope === "shape") st.r.f.size = floor;
                    else if (a.index !== undefined && st.subs[a.index]) st.subs[a.index].sf.size = floor;
                    raisedRuns++;
                  }
                  raisedShapes.add(`${s.id}:${st.r.sh.id}`);
                  mixedSkippedTotal += plan.mixedSkipped.length;
                  // Raising a size on an AUTO-GROW box pushes its bottom edge down
                  // (possible overlap); on a SHRINK box the nominal bump may not
                  // change what renders. Flag both so the agent verifies visually.
                  if (/shapetofittext/i.test(st.autofit)) growSlides.add(String(s.id));
                  if (isShrinkToFit(st.autofit)) shrinkShapes.add(`${s.id}:${st.r.sh.id}`);
                } catch {
                  /* one unreadable shape must not abort the sweep */
                }
              }
              // eslint-disable-next-line no-await-in-loop
              await ctx.sync().catch(() => {});
            }
            const bits: string[] = [
              `Raised ${raisedRuns} run(s) across ${raisedShapes.size} box(es) to ≥${floor}pt` +
                (only ? ` on slide ${only}` : " deck-wide") +
                ".",
            ];
            if (growSlides.size)
              bits.push(
                `${growSlides.size} slide(s) have auto-grow boxes that may now overlap — get_slide_image them and transform/shorten if so.`
              );
            if (shrinkShapes.size)
              bits.push(
                `${shrinkShapes.size} box(es) use shrink-to-fit autofit: the bump may not render bigger — shorten the text or set autosize "none".`
              );
            if (mixedSkippedTotal)
              bits.push(
                `${mixedSkippedTotal} mixed-size line(s) left untouched to avoid shrinking a larger run — set those explicitly if needed.`
              );
            notes.push({ op: "set_min_font", index: i, advice: bits.join(" ") });
            applied++;
            break;
          }
          case "org_chart":
          case "timeline":
          case "process_flow":
          case "structure_chart": {
            if (!api.v18) throw new Error(`${o.op} needs PowerPointApi 1.8 (grouping)`);
            const s = await targetSlide(o);
            const region = clampRegionToSafe(regionFromOp(o));
            const fitReport: any = o.op === "structure_chart" ? {} : undefined;
            // The model this composite is rendered from — stored on the group so
            // a later slide can read it back (read_slide returns shape tags),
            // mutate one thing, and re-render for a step-plan delta.
            const model =
              o.op === "org_chart"
                ? o.nodes
                : o.op === "timeline"
                ? o.events
                : o.op === "process_flow"
                ? o.steps
                : { entities: o.entities, ownership: o.ownership, flows: o.flows ?? [] };
            const prims: Primitive[] =
              o.op === "org_chart"
                ? orgChartLayout(o.nodes, region, DEFAULT_PALETTE)
                : o.op === "timeline"
                ? timelineLayout(o.events, region, DEFAULT_PALETTE)
                : o.op === "process_flow"
                ? processFlowLayout(o.steps, region, DEFAULT_PALETTE)
                : structureChartLayout(
                    { entities: o.entities, ownership: o.ownership, flows: o.flows },
                    region,
                    DEFAULT_PALETTE,
                    fitReport
                  );
            const members: PowerPoint.Shape[] = [];
            for (const p of prims) members.push(...createPrimitive(s.shapes, p));
            await ctx.sync();
            let groupId: string | undefined;
            if (members.length >= 2) {
              try {
                const g = (s.shapes as any).addGroup(members);
                g.tags.add("PF_KIND", o.op.toUpperCase());
                // Structure models are larger; give the round-trip tag room.
                g.tags.add("PF_MODEL", JSON.stringify(model).slice(0, 16000));
                g.load("id");
                await ctx.sync();
                groupId = g.id;
              } catch {
                /* grouping is best-effort — the shapes are already on the slide */
              }
            }
            created.push({ op: o.op, index: i, slide_id: s.id, shape_id: groupId });
            if (fitReport && fitReport.cramped) {
              notes.push({ op: o.op, index: i, fit: fitReport, advice: fitReport.advice });
            }
            applied++;
            break;
          }
          default:
            skipped.push({ op: o.op, index: i, reason: "not supported by the live bridge" });
            break;
        }
        // Commit this op on its own so a bad op is isolated (not fatal to the batch).
        await ctx.sync();
      } catch (e) {
        applied = beforeApplied;
        created.length = beforeCreated;
        skipped.push({
          op: o.op,
          index: i,
          slide: o.slide !== undefined ? String(o.slide) : undefined,
          shape: o.shape !== undefined ? String(o.shape) : undefined,
          reason: String((e as any)?.message ?? e),
        });
      }
    }

    return { applied, created, skipped, notes, replaced: replacedTotal };
  }).catch((e) => ({ applied: 0, created: [], skipped: [], notes: [], replaced: 0, error: String(e?.message ?? e) }));

  notifyIframe({
    type: "@office-plugin",
    action: "apply_ops_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...result,
  });
}

/* ── Write handler: insert_slides (DSL → PptxGenJS → insertSlidesFromBase64) ─ */

/** Shared core: inject a base64 .pptx into the open deck at a position. */
async function insertDeckBase64(
  b64: string,
  position: unknown,
  theme: unknown,
  sourceSlideIds?: string[]
): Promise<{ inserted: number; newSlideIds: string[] }> {
  return PowerPoint.run(async (ctx) => {
    const pres = ctx.presentation;
    const coll = pres.slides;
    coll.load("items/id");
    await ctx.sync();
    const beforeIds = new Set(coll.items.map((s) => s.id));

    // insertSlidesFromBase64: omitted targetSlideId inserts at the BEGINNING;
    // appending requires the last slide's id.
    const options: any = {
      formatting: theme === "source" ? "KeepSourceFormatting" : "UseDestinationTheme",
    };
    if (sourceSlideIds && sourceSlideIds.length) options.sourceSlideIds = sourceSlideIds;
    const pos = position ?? "end";
    if (pos === "end") {
      if (coll.items.length > 0) options.targetSlideId = `${coll.items[coll.items.length - 1].id}#`;
    } else if (pos !== "start") {
      const anchor = await resolveSlide(ctx, pos);
      options.targetSlideId = `${anchor.id}#`;
    }

    pres.insertSlidesFromBase64(b64, options);
    await ctx.sync();

    coll.load("items/id");
    await ctx.sync();
    const newSlideIds = coll.items.filter((s) => !beforeIds.has(s.id)).map((s) => s.id);
    return { inserted: newSlideIds.length, newSlideIds };
  });
}

async function handleInsertSlides(msg: any): Promise<void> {
  let out: any;
  try {
    const slides: DslSlide[] = msg.slides ?? [];
    if (!Array.isArray(slides) || slides.length === 0) throw new Error("slides must be a non-empty array");

    // Render OUTSIDE PowerPoint.run — PptxGenJS is a plain async browser call.
    const b64 = await renderSlidesToBase64(slides);
    out = await insertDeckBase64(b64, msg.position, msg.theme);
  } catch (e: any) {
    out = { inserted: 0, newSlideIds: [], error: String(e?.message ?? e) };
  }
  notifyIframe({
    type: "@office-plugin",
    action: "insert_slides_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...out,
  });
}

/**
 * Map 1-based slide numbers to the source deck's slide ids ("nnn#") by reading
 * the ordered <p:sldIdLst> out of ppt/presentation.xml (jszip ships with
 * pptxgenjs). insertSlidesFromBase64's sourceSlideIds needs ids, not indices.
 */
async function sourceIdsForIndices(b64: string, indices: number[]): Promise<string[]> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(b64, { base64: true });
  const presXml = await zip.file("ppt/presentation.xml")?.async("string");
  if (!presXml) throw new Error("source file is not a valid .pptx (no ppt/presentation.xml)");
  const ids: string[] = [];
  const re = /<p:sldId[^>]*\bid="(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(presXml))) ids.push(m[1]);
  const picked: string[] = [];
  for (const n of indices) {
    const idx = Number(n);
    if (!Number.isInteger(idx) || idx < 1 || idx > ids.length)
      throw new Error(`source deck has ${ids.length} slides — slide ${n} does not exist`);
    picked.push(`${ids[idx - 1]}#`);
  }
  return picked;
}

async function handleInsertFromBase64(msg: any): Promise<void> {
  let out: any;
  try {
    const b64 = String(msg.pptx_base64 ?? "");
    if (!b64) throw new Error("pptx_base64 is required");
    let sourceSlideIds: string[] | undefined;
    if (Array.isArray(msg.source_slides) && msg.source_slides.length > 0) {
      sourceSlideIds = await sourceIdsForIndices(b64, msg.source_slides);
    }
    out = await insertDeckBase64(b64, msg.position, msg.theme, sourceSlideIds);
  } catch (e: any) {
    out = { inserted: 0, newSlideIds: [], error: String(e?.message ?? e) };
  }
  notifyIframe({
    type: "@office-plugin",
    action: "insert_from_base64_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...out,
  });
}

/** Read the whole saved deck as base64 via getFileAsync(Compressed) slices. */
function getDeckAsBase64(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 4194304 }, (res) => {
      if (res.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(res.error?.message ?? "getFileAsync failed"));
        return;
      }
      const file = res.value;
      const slices: any[] = new Array(file.sliceCount);
      const fetchSlice = (i: number) => {
        file.getSliceAsync(i, (sr) => {
          if (sr.status !== Office.AsyncResultStatus.Succeeded) {
            file.closeAsync(() => {});
            reject(new Error(sr.error?.message ?? "getSliceAsync failed"));
            return;
          }
          slices[sr.value.index] = sr.value.data;
          const next = i + 1;
          if (next < file.sliceCount) {
            fetchSlice(next);
            return;
          }
          file.closeAsync(() => {});
          try {
            let bin = "";
            for (const s of slices) {
              const bytes = s as number[];
              let chunk = "";
              for (let b = 0; b < bytes.length; b++) chunk += String.fromCharCode(bytes[b]);
              bin += chunk;
            }
            resolve(window.btoa(bin));
          } catch (e: any) {
            reject(new Error(String(e?.message ?? e)));
          }
        });
      };
      fetchSlice(0);
    });
  });
}

async function handleExportDeck(msg: any): Promise<void> {
  let out: any;
  try {
    const b64 = await getDeckAsBase64();
    let title = "";
    try {
      title = await PowerPoint.run(async (ctx) => {
        ctx.presentation.load("title");
        await ctx.sync();
        return ctx.presentation.title;
      });
    } catch {
      /* title is optional */
    }
    out = { pptx_base64: b64, title };
  } catch (e: any) {
    out = { error: String(e?.message ?? e) };
  }
  notifyIframe({
    type: "@office-plugin",
    action: "export_deck_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...out,
  });
}

const THEME_SLOTS = [
  "dark1",
  "light1",
  "dark2",
  "light2",
  "accent1",
  "accent2",
  "accent3",
  "accent4",
  "accent5",
  "accent6",
  "hyperlink",
  "followedHyperlink",
];

async function handleGetTheme(msg: any): Promise<void> {
  const work = PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    const slide = slides.items[0];
    if (!slide) throw new Error("the deck has no slides yet");

    // Preferred path: the real theme color scheme (PowerPointApi 1.10).
    if (api.v110) {
      const scheme = (slide as any).themeColorScheme;
      const reads: { slot: string; r: any }[] = [];
      for (const slot of THEME_SLOTS) {
        try {
          reads.push({ slot, r: scheme.getThemeColor(slot) });
        } catch {
          /* unsupported slot name on this build */
        }
      }
      await ctx.sync();
      const colors: Record<string, string> = {};
      for (const { slot, r } of reads) {
        try {
          if (r?.value) colors[slot] = String(r.value);
        } catch {
          /* skip */
        }
      }
      if (Object.keys(colors).length > 0) return { colors, source: "theme_scheme" };
    }

    // Graceful fallback (#6): sample the actual solid fill and font colors used
    // by shapes across the first few slides — an approximate palette the agent
    // can match, so it is never left with no color information.
    const sampleSlides = slides.items.slice(0, 4);
    for (const s of sampleSlides) s.shapes.load("items/id,items/type");
    await ctx.sync();
    const fills: PowerPoint.ShapeFill[] = [];
    const fonts: PowerPoint.ShapeFont[] = [];
    for (const s of sampleSlides) {
      for (const sh of s.shapes.items) {
        try {
          const fill = sh.fill;
          fill.load("foregroundColor,type");
          fills.push(fill);
        } catch {
          /* skip */
        }
        if (isTextishType(sh.type)) {
          try {
            const font = sh.textFrame.textRange.font;
            font.load("color");
            fonts.push(font);
          } catch {
            /* skip */
          }
        }
      }
    }
    await ctx.sync().catch(() => {});
    const seen = new Set<string>();
    const sampled: string[] = [];
    for (const f of fills) {
      try {
        const c = (f as any).foregroundColor;
        if (c && /^#?[0-9a-fA-F]{6}$/.test(String(c))) {
          const hex = String(c).replace(/^#/, "").toUpperCase();
          if (!seen.has(hex)) {
            seen.add(hex);
            sampled.push(hex);
          }
        }
      } catch {
        /* skip */
      }
    }
    const fontColors: string[] = [];
    const seenFont = new Set<string>();
    for (const f of fonts) {
      try {
        const c = (f as any).color;
        if (c && /^#?[0-9a-fA-F]{6}$/.test(String(c))) {
          const hex = String(c).replace(/^#/, "").toUpperCase();
          if (!seenFont.has(hex)) {
            seenFont.add(hex);
            fontColors.push(hex);
          }
        }
      } catch {
        /* skip */
      }
    }
    if (sampled.length === 0 && fontColors.length === 0) {
      throw new Error("could not read theme colors and found no colored shapes to sample");
    }
    return { sampled_fills: sampled.slice(0, 12), sampled_font_colors: fontColors.slice(0, 8), source: "sampled" };
  });
  // Fast-fail race so a hung theme/color read can't stall the whole dispatch.
  const out = await Promise.race([
    work,
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            error:
              "reading the deck's colors did not return in time. Proceed with a restrained neutral " +
              "palette that will not clash, and match colors visible in read_slide shape styles where you can.",
            reason: "theme_timeout",
          }),
        20000
      )
    ),
  ]).catch((e: any) => ({ error: String(e?.message ?? e), reason: "theme_error" }));
  notifyIframe({
    type: "@office-plugin",
    action: "get_theme_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...(out as any),
  });
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function safeParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * The relay WebSocket drops any single client->server message over 32 KiB
 * (coder/websocket `defaultReadLimit`), silently: the tool process never gets a
 * reply and blocks until its own 120s dispatch timeout. The limit applies to the
 * reassembled message, so WebSocket frame fragmentation cannot get under it —
 * only splitting into separate logical messages does.
 *
 * Anything over SAFE goes out as N `result_chunk` messages that app.ppt:plugin
 * reassembles by request_id. Budget leaves room for the chunk envelope itself.
 */
const WS_SAFE_BYTES = 24000;
const CHUNK_BODY_BYTES = WS_SAFE_BYTES - 1024;

/** Slide-image budget. base64 is returned to the agent as TEXT, so an unbounded
 * PNG poisons the session context even when the transport succeeds. */
const DEFAULT_IMAGE_WIDTH = 480;
const MAX_IMAGE_WIDTH = 1100;
const MAX_IMAGE_B64_BYTES = 260_000;

const utf8 = new TextEncoder();
const byteLen = (s: string): number => utf8.encode(s).length;

/** Split on UTF-8 byte budget without ever cutting a surrogate pair in half. */
function chunkString(s: string, maxBytes: number): string[] {
  const chunks: string[] = [];
  let i = 0;
  while (i < s.length) {
    let end = Math.min(s.length, i + maxBytes);
    const notMidSurrogate = (e: number) => {
      const c = s.charCodeAt(e - 1);
      return !(c >= 0xd800 && c <= 0xdbff && e < s.length);
    };
    if (!notMidSurrogate(end)) end--;
    // A char can encode to up to 4 bytes, so shrink until the slice fits.
    while (end > i + 1 && byteLen(s.slice(i, end)) > maxBytes) {
      end = i + Math.max(1, Math.floor((end - i) / 2));
      if (!notMidSurrogate(end)) end--;
    }
    chunks.push(s.slice(i, end));
    i = end;
  }
  return chunks;
}

function postToIframe(message: Record<string, unknown>): void {
  const iframe = document.getElementById("chat-iframe") as HTMLIFrameElement;
  iframe?.contentWindow?.postMessage(message, CHAT_ORIGIN);
}

function notifyIframe(message: Record<string, unknown>): void {
  const json = JSON.stringify(message);
  const action = message.action as string | undefined;
  const requestId = message.request_id;
  const replyPid = message.reply_pid;

  // Small, or unroutable (no correlation id to reassemble against) — send as is.
  if (byteLen(json) <= WS_SAFE_BYTES || !action || !requestId || !replyPid) {
    postToIframe(message);
    return;
  }

  const { type: _t, action: _a, request_id: _r, reply_pid: _p, ...body } = message;
  const parts = chunkString(JSON.stringify(body), CHUNK_BODY_BYTES);
  // Keep the ORIGINAL action. Invented action names (`result_chunk`,
  // `<action>_chunk`) never arrive — the relay only forwards actions it knows,
  // so the chunks were dropped and the caller hung to its 120s timeout. Reusing
  // the real action keeps the message routable and means the relay's topic is
  // already the correct destination; `chunked` is what marks it for reassembly.
  parts.forEach((part, idx) =>
    postToIframe({
      type: "@office-plugin",
      action,
      request_id: requestId,
      reply_pid: replyPid,
      chunked: true,
      seq: idx + 1,
      total: parts.length,
      part,
    })
  );
}

/**
 * Salesforce OAuth popup — carried over verbatim from the Word add-in shell.
 * Invoked on demand when the iframe sends `open_auth`. The iframe otherwise
 * drives its own auth and signals completion via `ws_connected`.
 */
function openAuthDialog(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get("clientId") || "";
  const redirectUri = urlParams.get("redirectUri") || DEFAULT_REDIRECT_URI;
  const isSandbox = urlParams.get("isSandbox") === "true";
  const loginBaseUrl = isSandbox ? SANDBOX_LOGIN_URL : DEFAULT_LOGIN_URL;
  const loginUrl = `${loginBaseUrl}${LOGIN_PATH}`;

  const authUrl =
    clientId &&
    `${loginUrl}?response_type=token&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}`;

  const urlToOpen = authUrl || loginBaseUrl;
  const dialogOptions: Office.DialogOptions = { height: 60, width: 30 };

  Office.context.ui.displayDialogAsync(urlToOpen, dialogOptions, (asyncResult: Office.AsyncResult<Office.Dialog>) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      console.error("Error displaying dialog:", asyncResult.error?.message);
      notifyIframe({ type: "AUTH_POPUP_CLOSED" });
      return;
    }

    const dialog = asyncResult.value;
    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
      if ("error" in arg) return;
      try {
        const message = JSON.parse(arg.message);
        if (message.status === "success") {
          notifyIframe({ type: "AUTH_SUCCESS", token: message.token, instanceUrl: message.instanceUrl });
          dialog.close();
        } else {
          notifyIframe({ type: "AUTH_POPUP_CLOSED" });
          dialog.close();
        }
      } catch {
        notifyIframe({ type: "AUTH_POPUP_CLOSED" });
        dialog.close();
      }
    });

    dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
      notifyIframe({ type: "AUTH_POPUP_CLOSED" });
    });
  });
}
